
import React, { useEffect, useRef, useState } from 'react';
import { LineStyle, LineSeries, BaselineSeries, createSeriesMarkers } from 'lightweight-charts';
import { EMA, Extremums, Pivot, ATR } from '@debut/indicators';
import { convertCandles, intervalToMinutes } from '#src/utils'
import { TrendLine } from './plugins/TrendLine';
import { VertLine } from './plugins/VertLine';
import { kmeans } from 'ml-kmeans';
import dbscan from '@cdxoo/dbscan'

// ***********************************************
// view config
// ***********************************************
// filter 2
const similarLevelPercent = 0.002
// 
const simpleStatsEmaLength = 7
const waveDurationMult = 7 * 24 * 60 * 60 // 1 week in seconds
// 
const fiboHL = [0, 1]
const fiboCorrectionLevels = [0.236, 0.382, 0.5, 0.618, 0.786] // 0.236, 0.786, 1.618, 2, 2.618, 4.236]
const fiboUpExtension = [1.618, -0.236, -0.382, -0.5, -0.618, -0.786, -1, -1.236, -1.618]
const fiboDownExtension = [-0.618, 1.236, 1.382, 1.5, 1.618, 1.786, 2, 2.618]

// ***********************************************
// Calculate Waves
// ***********************************************

export class WaveFinder {
  constructor() {
    // Состояние - это кэш последних данных и результата
    this.lastLows = null;
    this.lastHighs = null;
    this.cachedResult = { levels: [], waves: [] };
  }

  update(lows, highs, candles, timeMap) {
    // Проверяем, изменились ли входные данные
    if (this.lastLows && this.lastHighs &&
      this.lastLows.length === lows.length &&
      this.lastHighs.length === highs.length &&
      this.lastLows[this.lastLows.length - 1]?.startTime === lows[lows.length - 1]?.startTime &&
      this.lastHighs[this.lastHighs.length - 1]?.startTime === highs[highs.length - 1]?.startTime
    ) {
      // Данные не изменились, возвращаем кэшированный результат
      return this.cachedResult;
    }

    // Данные изменились. Запускаем полный перерасчет.
    const result = getWaves(lows, highs, candles, timeMap);

    // Кэшируем новые данные и результат
    this.lastLows = lows;
    this.lastHighs = highs;
    this.cachedResult = result;

    return result;
  }
}

export function getWaves(lows, highs, candles, timeMap) {
  const levels = []
  const waves = []

  if (!lows.length || !highs.length) {
    return { levels, waves }
  }

  // ВАЖНО: Возможно сортировка здесь не нужна, так как данные уже приходят отсортированными.
  // lows.sort((a, b) => a.startTime - b.startTime)
  // highs.sort((a, b) => a.startTime - b.startTime)


  let lowIdx = 0
  let highIdx = 0
  let hhIndex = highIdx
  let llIndex = lowIdx

  let side = null
  if (lows[lowIdx].startTime <= highs[highIdx].startTime) {
    side = "low"
  } else {
    side = "high"
  }

  // console.log('getWaves initial side', side)

  while (lowIdx < lows.length && highIdx < highs.length) {
    if (side === "low") {
      if (lowIdx + 1 < lows.length) {
        let nextLowTime = lows[lowIdx + 1].startTime
        // find lowest low between current high (include it) and next low
        // update llIndex & nextLowTime if we found lower low
        const nextHighTime = highs[highIdx].startTime
        let llPrice = lows[lowIdx].level
        // console.log('check', side, lowIdx, llIndex, lows[lowIdx])
        for (let i = lowIdx + 1; i < lows.length - 1; i++) {
          if (lows[i].startTime > nextHighTime) {
            // console.log('next low in future', side, i, lows[i])
            lowIdx = i - 1
            break
          }
          // console.log('have another', side, i, lowIdx, llIndex, lows[i])
          if (lows[i].level < llPrice) {
            // lowIdx = i
            llIndex = i
            llPrice = lows[i].level
            nextLowTime = lows[i + 1].startTime
            // console.log('selected better', side, lowIdx, llIndex, lows[lowIdx])
          }
        }
        // console.log('selected', side, lowIdx, llIndex, lows[llIndex])
        // console.log('next', side, lowIdx + 1, lows[lowIdx + 1])
        nextLowTime = lows[lowIdx + 1].startTime
        // find highest high between current high (include it) and next low
        hhIndex = highIdx
        let hhPrice = highs[highIdx].level
        while (highIdx + 1 < highs.length && highs[highIdx + 1].startTime < nextLowTime) {
          highIdx++
          if (highs[highIdx].level > hhPrice) {
            hhIndex = highIdx
            hhPrice = highs[highIdx].level
          }
          // console.log('while high', highIdx, hhIndex, highs[highIdx])
        }
        highs[hhIndex]._type = "high"
        levels.push(highs[hhIndex])
        // console.log('add high', hhIndex, highs[hhIndex])
        const delta = highs[hhIndex].level - lows[llIndex].level
        const deltaPercent = delta / lows[llIndex].level

        const wave = {
          direction: "up",
          low: lows[llIndex].level,
          high: highs[hhIndex].level,
          delta: delta,
          deltaPercent,
          start: lows[llIndex].startTime,
          end: highs[hhIndex].startTime,
          lowLevelIdx: llIndex,
          highLevelIdx: hhIndex,
          // lowLevel: lows[llIndex],
          // highLevel: highs[hhIndex],
        }

        const startIndex = timeMap.get(wave.start);
        const endIndex = timeMap.get(wave.end);
        let totalVolume = 0;
        if (startIndex !== undefined && endIndex !== undefined && startIndex <= endIndex) {
          for (let i = startIndex; i <= endIndex; i++) {
            totalVolume += candles[i]?.volume || 0;
          }
        }
        wave.totalVolume = totalVolume;

        const channel = calculateWaveChannel(wave, candles, timeMap);
        // wave.maxDiff = channel.maxDiff
        // wave.maxDiffUp = channel.maxDiffUp
        // wave.maxDiffDn = channel.maxDiffDn
        wave.maxDiffPercent = channel.maxDiffPercent
        wave.maxDiffUpPercent = channel.maxDiffUpPercent
        wave.maxDiffDnPercent = channel.maxDiffDnPercent
        waves.push(wave)
      }
      side = "high"
      lowIdx++
    } else if (side === "high") {
      if (highIdx + 1 < highs.length) {
        let nextHighTime = highs[highIdx + 1].startTime
        // find higest high between current high (include it) and next low
        // update hhIndex & nextHighTime if we found higher high
        const nextLowTime = lows[lowIdx].startTime
        let hhPrice = highs[highIdx].level
        // console.log('check', side, highIdx, hhIndex, highs[highIdx])

        // use >= && <= to include current high and low (hotfix
        for (let i = highIdx + 1; i < highs.length - 1; i++) {
          if (highs[i].startTime >= nextLowTime) {
            // console.log('next high in future', side, i, highs[i])
            highIdx = i - 1
            break
          }
          // console.log('have another', side, i, highIdx, hhIndex, highs[i])
          if (highs[i].level > hhPrice) {
            // highIdx = i
            hhIndex = i
            hhPrice = highs[i].level
            nextHighTime = highs[i + 1].startTime
            // console.log('selected better', side, highIdx, hhIndex, highs[highIdx])
          }
        }
        // console.log('selected', side, highIdx, hhIndex, highs[hhIndex])
        // console.log('next', side, highIdx + 1, highs[highIdx + 1])
        nextHighTime = highs[highIdx + 1].startTime

        llIndex = lowIdx
        let llPrice = lows[lowIdx].level
        while (lowIdx + 1 < lows.length && lows[lowIdx + 1].startTime <= nextHighTime) {
          lowIdx++
          if (lows[lowIdx].level < llPrice) {
            llIndex = lowIdx
            llPrice = lows[lowIdx].level
          }
          // console.log('while low', lowIdx, llIndex, lows[lowIdx])
        }
        lows[llIndex]._type = "low"
        levels.push(lows[llIndex])
        // console.log('add low', llIndex, lows[llIndex])
        const delta = highs[hhIndex].level - lows[llIndex].level
        const deltaPercent = delta / highs[hhIndex].level
        const wave = {
          direction: "down",
          low: lows[llIndex].level,
          high: highs[hhIndex].level,
          delta,
          deltaPercent,
          start: highs[hhIndex].startTime,
          end: lows[llIndex].startTime,
          lowLevelIdx: llIndex,
          highLevelIdx: hhIndex,
          // lowLevel: lows[llIndex],
          // highLevel: highs[hhIndex],
        }
        const startIndex = timeMap.get(wave.start);
        const endIndex = timeMap.get(wave.end);
        let totalVolume = 0;
        if (startIndex !== undefined && endIndex !== undefined && startIndex <= endIndex) {
          for (let i = startIndex; i <= endIndex; i++) {
            totalVolume += candles[i]?.volume || 0;
          }
        }
        wave.totalVolume = totalVolume;

        const channel = calculateWaveChannel(wave, candles, timeMap);
        // wave.maxDiff = channel.maxDiff
        // wave.maxDiffUp = channel.maxDiffUp
        // wave.maxDiffDn = channel.maxDiffDn
        wave.maxDiffPercent = channel.maxDiffPercent
        wave.maxDiffUpPercent = channel.maxDiffUpPercent
        wave.maxDiffDnPercent = channel.maxDiffDnPercent
        waves.push(wave)
      }
      side = "low"
      highIdx++
    }
  }

  // console.log('getWaves', side, 'low', llIndex, lowIdx, lows.length, 'high', hhIndex, highIdx, highs.length)

  // Проверка на оставшиеся элементы после основного цикла
  if (lowIdx < lows.length || highIdx < highs.length) {
    if (side === "low" && lowIdx < lows.length) {
      // Выбираем самый низкий low из оставшихся
      const remainingLows = lows.slice(lowIdx);
      const lowestLow = remainingLows.reduce((min, low, idx) => {
        return low.level < min.level ? { level: low.level, idx: lowIdx + idx } : min;
      }, { level: remainingLows[0].level, idx: lowIdx });

      if (highIdx > 0) {
        const lastHighIdx = hhIndex;
        const delta = highs[lastHighIdx].level - lowestLow.level;
        const deltaPercent = delta / lowestLow.level;
        const wave = {
          direction: "down",
          low: lowestLow.level,
          high: highs[lastHighIdx].level,
          delta,
          deltaPercent,
          start: highs[lastHighIdx].startTime,
          end: lows[lowestLow.idx].startTime,
          lowLevelIdx: lowestLow.idx,
          highLevelIdx: lastHighIdx
        }

        const startIndex = timeMap.get(wave.start);
        const endIndex = timeMap.get(wave.end);
        let totalVolume = 0;
        if (startIndex !== undefined && endIndex !== undefined && startIndex <= endIndex) {
          for (let i = startIndex; i <= endIndex; i++) {
            totalVolume += candles[i]?.volume || 0;
          }
        }
        wave.totalVolume = totalVolume;

        const channel = calculateWaveChannel(wave, candles, timeMap);
        // wave.maxDiff = channel.maxDiff
        // wave.maxDiffUp = channel.maxDiffUp
        // wave.maxDiffDn = channel.maxDiffDn
        wave.maxDiffPercent = channel.maxDiffPercent
        wave.maxDiffUpPercent = channel.maxDiffUpPercent
        wave.maxDiffDnPercent = channel.maxDiffDnPercent
        waves.push(wave);
        lows[lowestLow.idx]._type = "low";
        levels.push(lows[lowestLow.idx]);
        // console.log('add low', lowestLow.idx, lows[lowestLow.idx])
      }
    } else if (side === "high" && highIdx < highs.length) {
      // Выбираем самый высокий high из оставшихся
      const remainingHighs = highs.slice(highIdx);
      const highestHigh = remainingHighs.reduce((max, high, idx) => {
        return high.level > max.level ? { level: high.level, idx: highIdx + idx } : max;
      }, { level: remainingHighs[0].level, idx: highIdx });

      if (lowIdx > 0) {
        const lastLowIdx = llIndex;
        const delta = highestHigh.level - lows[lastLowIdx].level;
        const deltaPercent = delta / highestHigh.level;
        const wave = {
          direction: "up",
          low: lows[lastLowIdx].level,
          high: highestHigh.level,
          delta,
          deltaPercent,
          start: lows[lastLowIdx].startTime,
          end: highs[highestHigh.idx].startTime,
          lowLevelIdx: lastLowIdx,
          highLevelIdx: highestHigh.idx
        }

        const startIndex = timeMap.get(wave.start);
        const endIndex = timeMap.get(wave.end);
        let totalVolume = 0;
        if (startIndex !== undefined && endIndex !== undefined && startIndex <= endIndex) {
          for (let i = startIndex; i <= endIndex; i++) {
            totalVolume += candles[i]?.volume || 0;
          }
        }
        wave.totalVolume = totalVolume;

        const channel = calculateWaveChannel(wave, candles, timeMap);
        // wave.maxDiff = channel.maxDiff
        // wave.maxDiffUp = channel.maxDiffUp
        // wave.maxDiffDn = channel.maxDiffDn
        wave.maxDiffPercent = channel.maxDiffPercent
        wave.maxDiffUpPercent = channel.maxDiffUpPercent
        wave.maxDiffDnPercent = channel.maxDiffDnPercent
        waves.push(wave);
        highs[highestHigh.idx]._type = "high";
        levels.push(highs[highestHigh.idx]);
        // console.log('add high', highestHigh.idx, highs[highestHigh.idx])
      }
    }
  }

  markWaves(waves)

  return { levels, waves }
}

/**
 * Вычисляет максимальное отклонение цены от центральной линии волны.
 * @param {object} wave - Объект волны (должен содержать start, end, low, high, direction).
 * @param {Array<object>} candles - Полный массив свечей.
 * @returns {{maxDiffUp: number, maxDiffDn: number}}
 */
function calculateWaveChannel(wave, candles, timeMap) {
  if (!candles?.length) {
    // ИЗМЕНЕНИЕ: Возвращаем все четыре поля
    return {
      maxDiff: 0, maxDiffUp: 0, maxDiffDn: 0,
      maxDiffPercent: 0, maxDiffUpPercent: 0, maxDiffDnPercent: 0
    };
  }

  const startIndex = timeMap.get(wave.start);
  const endIndex = timeMap.get(wave.end);

  // Проверка, что индексы нашлись (на случай рассинхронизации данных)
  if (startIndex === undefined || endIndex === undefined || startIndex >= endIndex) {
    return {
      maxDiff: 0, maxDiffUp: 0, maxDiffDn: 0,
      maxDiffPercent: 0, maxDiffUpPercent: 0, maxDiffDnPercent: 0
    };
  }

  const startPrice = wave.direction === 'up' ? wave.low : wave.high;
  const endPrice = wave.direction === 'up' ? wave.high : wave.low;
  const timeDuration = wave.end - wave.start;

  let maxDiffUp = 0;
  let maxDiffDn = 0;
  // ИЗМЕНЕНИЕ: Добавляем переменные для процентных отклонений
  let maxDiffUpPercent = 0;
  let maxDiffDnPercent = 0;

  // Проходим по всем свечам внутри волны
  for (let i = startIndex; i <= endIndex; i++) {
    const candle = candles[i];

    const timeProgress = timeDuration === 0 ? 1 : (candle.time - wave.start) / timeDuration;
    const centerlinePrice = startPrice + timeProgress * (endPrice - startPrice);

    // Абсолютное отклонение (без изменений)
    const diff_up = candle.high - centerlinePrice;
    const diff_dn = centerlinePrice - candle.low;

    // Обновляем абсолютные максимумы
    maxDiffUp = Math.max(maxDiffUp, diff_up);
    maxDiffDn = Math.max(maxDiffDn, diff_dn);

    // ИЗМЕНЕНИЕ: Считаем и обновляем процентные отклонения
    // Добавляем защиту от деления на ноль, если цена может быть 0
    const diffUpPercent = centerlinePrice === 0 ? 0 : (diff_up / centerlinePrice);
    const diffDnPercent = centerlinePrice === 0 ? 0 : (diff_dn / centerlinePrice);

    maxDiffUpPercent = Math.max(maxDiffUpPercent, diffUpPercent);
    maxDiffDnPercent = Math.max(maxDiffDnPercent, diffDnPercent);
  }

  const maxDiff = Math.max(maxDiffUp, maxDiffDn);
  const maxDiffPercent = Math.max(maxDiffUpPercent, maxDiffDnPercent);

  return { maxDiff, maxDiffUp, maxDiffDn, maxDiffPercent, maxDiffUpPercent, maxDiffDnPercent };
}

function markWaves(waves) {
  for (let i = 1; i < waves.length; i++) {
    const prevWave = waves[i - 1]
    const wave = waves[i]

    if (prevWave.direction === 'up' && wave.direction === 'down') {
      wave.type = wave.low > prevWave.low ? 'pullback' : 'move';
    } else if (prevWave.direction === 'down' && wave.direction === 'up') {
      wave.type = wave.high < prevWave.high ? 'pullback' : 'move'
    }
  }
}

function isSubWaveOfAny(masterId, waves) {
  if (!waves) return false;
  return waves.some(wave => {
    return wave.idx === masterId || (wave.corrections && isSubWaveOfAny(masterId, wave.corrections)) || (wave.waves && isSubWaveOfAny(masterId, wave.waves));
  });
}

function findSubWaves(masterIdx, masterId, waves, prefix = '') {
  let subWaves = [];
  const masterWave = waves[masterIdx];
  for (let i = masterIdx + 1; i < waves.length; i++) {
    const wave = waves[i];
    if (wave.low < masterWave.low || wave.high > masterWave.high) {
      break;
    }
    if (wave.low >= masterWave.low && wave.high <= masterWave.high) {
      const idx = prefix ? `${prefix}:${i}` : `${i}`;
      const isSubwave = isSubWaveOfAny(idx, subWaves);
      if (!isSubwave) {
        const subSubWaves = findSubWaves(i, idx, waves, prefix);
        const correctionWave = {
          ...wave,
          idx,
          masterId,
        };
        if (subSubWaves && subSubWaves.length) {
          if (correctionWave.corrections) {
            // console.log('createWaveHierarchy already has corrections', correctionWave.idx, correctionWave.corrections, subSubWaves)
            correctionWave.corrections.push(...subSubWaves)
          } else {
            correctionWave.corrections = subSubWaves
          }

          const correctionDetails = analyzeCorrections(correctionWave.corrections);
          const low = correctionWave.direction === 'up' ? correctionDetails.low : correctionWave.low
          const high = correctionWave.direction === 'up' ? correctionWave.high : correctionDetails.high
          const delta = high - low
          const deltaPercent = correctionWave.direction === 'up' ? delta / low : delta / high
          correctionWave.correction = {
            start: correctionWave.corrections[0].start,
            end: correctionDetails.end,
            low,
            high,
            delta,
            deltaPercent,
            ratioParent: delta / correctionWave.delta,
          };
        }
        subWaves.push(correctionWave);
      }
    }
  }
  return subWaves;
}

export function createWaveHierarchy(waves, prefix = '') {
  let hierarchy = [];
  for (let i = 0; i < waves.length; i++) {
    const idx = prefix ? `${prefix}:${i}` : `${i}`;
    const isSubwave = isSubWaveOfAny(idx, hierarchy);
    if (!isSubwave) {
      const wave = waves[i];
      const subWaves = findSubWaves(i, idx, waves, prefix);
      const sub = subWaves.length ? subWaves : wave.corrections || wave.waves;
      const w = {
        ...wave,
        idx,
      };
      if (sub && sub.length) {
        if (w.corrections) {
          // find new unique corrections & join if already have
          const wstarts = w.corrections.map(w => w.start)
          const newSubs = sub.filter(s => !wstarts.includes(s.start))
          if (newSubs.length) {
            w.corrections.push(...newSubs)
          }
        }
        else {
          w.corrections = sub
        }
      }
      hierarchy.push(w);
    }
  }

  for (let i = 0; i < hierarchy.length; i++) {
    const wave = hierarchy[i];
    if (wave.corrections) {
      const compresedCorrections = compressWaveCascades(wave.corrections, 10)
      if (compresedCorrections.length) {
        wave.corrections = compresedCorrections[compresedCorrections.length - 1].hierarchy

        const correctionDetails = analyzeCorrections(wave.corrections);
        const low = wave.direction === 'up' ? correctionDetails.low : wave.low
        const high = wave.direction === 'up' ? wave.high : correctionDetails.high
        const delta = high - low
        const deltaPercent = wave.direction === 'up' ? delta / low : delta / high
        wave.correction = {
          start: wave.corrections[0].start,
          end: correctionDetails.end,
          low,
          high,
          delta,
          deltaPercent,
          ratioParent: delta / wave.delta,
        };
      }
    }
  }

  return hierarchy;
}

export function analyzeCorrections(corrections) {
  let high = -Infinity;
  let low = Infinity;
  let start = corrections[0].start;
  let end = corrections[0].end;

  corrections.forEach(correction => {
    if (correction.high > high) high = correction.high;
    if (correction.low < low) low = correction.low;
    if (correction.end > end) end = correction.end;
    // Рекурсивно обновляем `end`, если есть вложенные коррекции
    if (correction.corrections && correction.corrections.length) {
      const nestedCorrection = analyzeCorrections(correction.corrections);
      if (nestedCorrection.end > end) end = nestedCorrection.end;
      if (nestedCorrection.high > high) high = nestedCorrection.high;
      if (nestedCorrection.low < low) low = nestedCorrection.low;
    }
  });

  return { start, end, high, low };
}

function getWaveCascades(wavesHierarchy, prefix = '') {
  const waveCascades = [];
  let currentCascade = [];
  let currentDirection = null;

  for (let i = 0; i < wavesHierarchy.length; i++) {
    if (!currentCascade.length) {
      currentCascade.push(wavesHierarchy[i]);
      currentDirection = wavesHierarchy[i].direction;
      continue;
    }

    const isNewDirection = currentDirection !== wavesHierarchy[i].direction;

    if (isNewDirection) {
      addCascade(waveCascades, currentCascade, currentDirection, i, prefix);
      currentCascade = [wavesHierarchy[i]];
      currentDirection = wavesHierarchy[i].direction;
    } else {
      currentCascade.push(wavesHierarchy[i]);
    }
  }

  if (currentCascade.length) {
    addCascade(waveCascades, currentCascade, currentDirection, wavesHierarchy.length, prefix, true);
  }

  markWaves(waveCascades);
  return waveCascades;
}

function addCascade(waveCascades, currentCascade, currentDirection, index, prefix) {
  if (currentCascade.length > 1) {
    const high = currentDirection === "up" ? currentCascade[currentCascade.length - 1].high : currentCascade[0].high;
    const low = currentDirection === "up" ? currentCascade[0].low : currentCascade[currentCascade.length - 1].low;
    const delta = high - low;
    const deltaPercent = currentDirection === "up" ? delta / low : delta / high;
    const idx = prefix ? `${prefix}:${index}` : `${index}`;
    const cas = {
      direction: currentDirection,
      idx,
      high,
      low,
      delta,
      deltaPercent,
      start: currentCascade[0].start,
      end: currentCascade[currentCascade.length - 1].end,
      segments: currentCascade
    }

    if (currentCascade[currentCascade.length - 1].corrections) {
      cas.corrections = currentCascade[currentCascade.length - 1].corrections
      cas.correction = currentCascade[currentCascade.length - 1].correction
    }

    waveCascades.push(cas);
  } else if (currentCascade.length === 1) {
    // Directly add the single wave object instead of wrapping it into another layer
    waveCascades.push(currentCascade[0]);
  }
}

export function compressWaveCascades(waveHierarchy, maxDepth = 10) {
  const cascadeStages = []

  let cascadeLength = Infinity
  let cascadesHierarchyLength = Infinity
  let currentWaveHierarchy = waveHierarchy

  for (let i = 0; cascadeLength > 1 && cascadesHierarchyLength > 1; i++) {
    if (i > maxDepth) {
      // console.warn('compressWaveCascades max depth reached', i)
      break
    }
    const waveCascades = getWaveCascades(currentWaveHierarchy, `X${i}`)
    // console.log('waveCascades', i, waveCascades)
    const waveCascadesHierarchy = createWaveHierarchy(waveCascades, `S${i}`)
    // console.log('waveCascadesHierarchy', i, waveCascadesHierarchy)
    currentWaveHierarchy = waveCascadesHierarchy

    // for (let j = 0; j < waveCascadesHierarchy.length; j++) {
    //   const wave = waveCascadesHierarchy[j]
    //   if (wave.corrections && wave.corrections.length) {
    //     const compresedCorrections = compressWaveCascades(wave.corrections, maxDepth)
    //     // wave.corrections = compresedCorrections[compresedCorrections.length - 1].hierarchy
    //     wave.compresedCorrections3 = compresedCorrections[compresedCorrections.length - 1].hierarchy
    //   }
    // }

    if (cascadeStages.length) {
      const lastCascade = cascadeStages[cascadeStages.length - 1]
      const { cascades, hierarchy } = lastCascade

      const isSameCascades = cascades.length === waveCascades.length
      const isSameHierarchy = hierarchy.length === waveCascadesHierarchy.length
      const noMoreCascades = waveCascades.length === hierarchy.length

      if (isSameCascades || isSameHierarchy || noMoreCascades) {
        // console.log('compressWaveCascades noMoreCascades', i, isSameCascades, isSameHierarchy, noMoreCascades)
        break
      } else {
        cascadeLength = waveCascades.length
        cascadesHierarchyLength = waveCascadesHierarchy.length
        const stage = { cascades: waveCascades, hierarchy: waveCascadesHierarchy }
        cascadeStages.push(stage)
      }
    } else {
      cascadeLength = waveCascades.length
      cascadesHierarchyLength = waveCascadesHierarchy.length
      const stage = { cascades: waveCascades, hierarchy: waveCascadesHierarchy }
      cascadeStages.push(stage)
    }
  }

  return cascadeStages
}

export function getWavesTrendLines(waves, endTime) {
  if (waves.length < 3) return { low: null, high: null };

  const w1 = waves[waves.length - 3];
  const w2 = waves[waves.length - 1];

  // Различие в расчёте времени для highs и lows в зависимости от направления первой волны
  const totalTimeHighs = w1.direction === 'up' ? (w2.end - w1.end) : (w2.start - w1.start);
  const totalTimeLows = w1.direction === 'up' ? (w2.start - w1.start) : (w2.end - w1.end);

  // Вычисление наклона и конечных точек
  const slopeHigh = (w2.high - w1.high) / totalTimeHighs;
  const slopeLow = (w2.low - w1.low) / totalTimeLows;

  const highsEndPrice = w1.direction === 'up'
    ? w2.high + slopeHigh * (endTime - w2.end)
    : w2.high + slopeHigh * (endTime - w2.start);

  const lowsEndPrice = w1.direction === 'up'
    ? w2.low + slopeLow * (endTime - w2.start)
    : w2.low + slopeLow * (endTime - w2.end);

  return {
    high: {
      start: {
        time: w1.direction === 'up' ? w1.end : w1.start,
        price: w1.high
      },
      end: {
        time: endTime,
        price: highsEndPrice
      }
    },
    low: {
      start: {
        time: w1.direction === 'up' ? w1.start : w1.end,
        price: w1.low
      },
      end: {
        time: endTime,
        price: lowsEndPrice
      }
    }
  };
}

// levels
// ***********************************************

export function getImportantLevelColor(level, alpha = 1) {
  if (level.type === 'resistance') {
    return `rgba(255,0,0,${alpha})`; // Red for high levels
  } else if (level.type === 'support') {
    return `rgba(0,255,0,${alpha})`; // Green for low levels
  } else if (level.type === 'fibo') {
    return `rgba(255,255,255,${alpha})`; // white for fibo levels
  } else {
    return `rgba(255,0,255,${alpha})`; // Blue for double levels
  }
}
// fibo

export function getBacktestTradesSeries(chart) {
  const series = []


  const buySeriesLong = chart.addSeries(LineSeries, {
    color: 'rgba(0,255,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    title: `BUY Long`,
  });
  series.push(buySeriesLong)

  const sellSeriesLong = chart.addSeries(LineSeries, {
    color: 'rgba(255,0,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    title: `SELL Long`,
  });
  series.push(sellSeriesLong)

  const buySeriesShort = chart.addSeries(LineSeries, {
    color: 'rgba(0,255,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    // title: `BUY`,
  });
  series.push(buySeriesShort)

  const sellSeriesShort = chart.addSeries(LineSeries, {
    color: 'rgba(255,0,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    // title: `SELL`,
  });
  series.push(sellSeriesShort)

  const buySeriesDataLong = [];
  const sellSeriesDataLong = [];
  const buySeriesDataShort = [];
  const sellSeriesDataShort = [];

  return {
    series,
    buySeriesLong,
    sellSeriesLong,
    buySeriesShort,
    sellSeriesShort,
    buySeriesDataLong,
    sellSeriesDataLong,
    buySeriesDataShort,
    sellSeriesDataShort,
  }
}

export function getBacktestTradesSeriesHorizontal(chart) {
  const series = []


  const buySeriesLong = chart.addSeries(LineSeries, {
    color: 'rgba(0,255,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    title: `BUY Long`,
  });
  series.push(buySeriesLong)

  const sellSeriesLong = chart.addSeries(LineSeries, {
    color: 'rgba(255,0,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    title: `SELL Long`,
  });
  series.push(sellSeriesLong)

  const buySeriesShort = chart.addSeries(LineSeries, {
    color: 'rgba(0,255,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    // title: `BUY`,
  });
  series.push(buySeriesShort)

  const sellSeriesShort = chart.addSeries(LineSeries, {
    color: 'rgba(255,0,0,0.9)',
    lineWidth: 1,
    lineStyle: LineStyle.Solid,
    lastValueVisible: false,
    priceLineVisible: false,
    axisLabelVisible: false,
    // title: `SELL`,
  });
  series.push(sellSeriesShort)

  const buySeriesDataLong = [];
  const sellSeriesDataLong = [];
  const buySeriesDataShort = [];
  const sellSeriesDataShort = [];

  return {
    series,
    buySeriesLong,
    sellSeriesLong,
    buySeriesShort,
    sellSeriesShort,
    buySeriesDataLong,
    sellSeriesDataLong,
    buySeriesDataShort,
    sellSeriesDataShort,
  }
}

// **********************************
//  levels
// **********************************

export class KeyLevelsFinder {
  constructor(minutesInCandle, atr, initAtr) {
    this.minutesInCandle = minutesInCandle;
    this.atr = atr;
    this.initAtr = initAtr;

    this.highLevels = [];
    this.lowLevels = [];
    this.highStack = []; // Стек индексов для high-уровней
    this.lowStack = [];  // Стек индексов для low-уровней
    this.processedCandlesCount = 0;
  }

  /**
   * Обновляет уровни на основе новых свечей.
   * @param {Array} newCandles - Массив новых свечей для обработки.
   * @returns {{highLevels: Array, lowLevels: Array}}
   */
  update(newCandles) {
    if (!newCandles || newCandles.length === 0) {
      // Если нет новых свечей, просто вернем текущие данные,
      // обновив длительность для непобитых уровней.
      this._updateUnbeatenDurations();
      return { highLevels: this.highLevels, lowLevels: this.lowLevels };
    }

    // --- ИСПРАВЛЕНИЕ: Шаг 1 ---
    // Сбрасываем флаги 'unbeat' у уровней, которые были в стеке до прихода новых данных.
    // Новые свечи могут их "побить", поэтому они больше не "непобитые".
    this.highStack.forEach(idx => {
      if (this.highLevels[idx]) {
        this.highLevels[idx].unbeat = false;
        // Длительность будет пересчитана, если уровень побьют или в конце этого вызова.
      }
    });
    this.lowStack.forEach(idx => {
      if (this.lowLevels[idx]) {
        this.lowLevels[idx].unbeat = false;
      }
    });

    // Шаг 2: Обрабатываем только новые свечи (эта логика была верной)
    for (const candle of newCandles) {
      const i = this.processedCandlesCount; // Глобальный индекс

      const candleAtr = this.atr.nextValue(candle.open, candle.close, candle.high, candle.low) || this.initAtr;

      while (this.highStack.length > 0 && candle.high > this.highLevels[this.highStack[this.highStack.length - 1]].level) {
        let idx = this.highStack.pop();
        this.highLevels[idx].duration = i - this.highLevels[idx].idx;
      }
      this.highStack.push(i);
      this.highLevels.push({ level: candle.high, duration: 0, minutesInCandle: this.minutesInCandle, startTime: candle.time, idx: i, atr: candleAtr });

      while (this.lowStack.length > 0 && candle.low < this.lowLevels[this.lowStack[this.lowStack.length - 1]].level) {
        let idx = this.lowStack.pop();
        this.lowLevels[idx].duration = i - this.lowLevels[idx].idx;
      }
      this.lowStack.push(i);
      this.lowLevels.push({ level: candle.low, duration: 0, minutesInCandle: this.minutesInCandle, startTime: candle.time, idx: i, atr: candleAtr });

      this.processedCandlesCount++;
    }

    // --- ИСПРАВЛЕНИЕ: Шаг 3 ---
    // Обновляем длительность для уровней, которые остались в стеке, но НЕ очищаем стек.
    this._updateUnbeatenDurations();

    return { highLevels: this.highLevels, lowLevels: this.lowLevels };
  }

  /**
   * Вспомогательный метод для обновления длительности и флагов для "непобитых" уровней.
   * @private
   */
  _updateUnbeatenDurations() {
    // Обновляем High-уровни, не извлекая их из стека
    this.highStack.forEach(idx => {
      const level = this.highLevels[idx];
      if (level) {
        level.duration = this.processedCandlesCount - level.idx;
        level.unbeat = true;
      }
    });

    // Обновляем Low-уровни, не извлекая их из стека
    this.lowStack.forEach(idx => {
      const level = this.lowLevels[idx];
      if (level) {
        level.duration = this.processedCandlesCount - level.idx;
        level.unbeat = true;
      }
    });
  }
}
export class KeyLevelFilter {
  constructor(windowSize, touchesAtrMult) {
    this.windowSize = windowSize;
    this.touchesAtrMult = touchesAtrMult;

    // --- Состояние: Храним только ИНДЕКСЫ и флаги обработки ---
    this.filteredHighIdxs = [];
    this.filteredLowIdxs = [];

    this.processedHighIdxs = new Set();
    this.processedLowIdxs = new Set();

    this.lastHighLevelsLength = 0;
    this.lastLowLevelsLength = 0;
  }

  update({ highLevels, lowLevels }) {
    // Теперь _updateSide модифицирует touches прямо в массивах highLevels/lowLevels
    this._updateSide(highLevels, 'high');
    this._updateSide(lowLevels, 'low');

    // В конце собираем результат из актуальных данных по сохраненным индексам
    const finalHighs = this.filteredHighIdxs.map(idx => highLevels[idx]);
    const finalLows = this.filteredLowIdxs.map(idx => lowLevels[idx]);

    return { filteredHighs: finalHighs, filteredLows: finalLows };
  }

  _updateSide(allLevels, side) {
    const filteredIdxs = side === 'high' ? this.filteredHighIdxs : this.filteredLowIdxs;
    const processedIdxs = side === 'high' ? this.processedHighIdxs : this.processedLowIdxs;
    const lastLevelsLength = side === 'high' ? this.lastHighLevelsLength : this.lastLowLevelsLength;
    const isHigh = side === 'high';

    // 1. Обновление касаний для УЖЕ найденных уровней
    // Проходим по сохраненным индексам
    for (const levelIdx of filteredIdxs) {
      const filteredLevel = allLevels[levelIdx]; // Берем АКТУАЛЬНЫЙ объект

      if (filteredLevel && !filteredLevel.isBroken) {
        const delta = filteredLevel.atr * this.touchesAtrMult || 0;
        const threshold = isHigh ? filteredLevel.level - delta : filteredLevel.level + delta;

        for (let i = lastLevelsLength; i < allLevels.length; i++) {
          const newLevel = allLevels[i];
          if ((isHigh && newLevel.level > filteredLevel.level) || (!isHigh && newLevel.level < filteredLevel.level)) {
            filteredLevel.isBroken = true; // Модифицируем свойство в оригинальном объекте
            break;
          }
          if ((isHigh && newLevel.level >= threshold) || (!isHigh && newLevel.level <= threshold)) {
            if (!filteredLevel.touches) filteredLevel.touches = 1;
            filteredLevel.touches++;
          }
        }
      }
    }

    // 2. Поиск НОВЫХ уровней
    for (let i = this.windowSize; i < allLevels.length; i++) {
      const currentLevel = allLevels[i];

      if (processedIdxs.has(currentLevel.idx)) {
        continue;
      }

      if (currentLevel.duration > 1) {
        let isExtremum = true;
        for (let j = 1; j <= this.windowSize; j++) {
          if ((isHigh && allLevels[i - j].level >= currentLevel.level) || (!isHigh && allLevels[i - j].level <= currentLevel.level)) {
            isExtremum = false;
            break;
          }
        }

        if (isExtremum) {
          // --- ГЛАВНОЕ ИЗМЕНЕНИЕ ---
          // Мы не создаем копию. Мы просто решаем, что этот уровень нам подходит.
          // Добавляем ему начальные 'touches' и 'isBroken'
          currentLevel.touches = 1;
          currentLevel.isBroken = false;

          const delta = currentLevel.atr * this.touchesAtrMult || 0;
          const threshold = isHigh ? currentLevel.level - delta : currentLevel.level + delta;

          for (let k = i + 1; k < allLevels.length; k++) {
            if ((isHigh && allLevels[k].level > currentLevel.level) || (!isHigh && allLevels[k].level < currentLevel.level)) {
              currentLevel.isBroken = true;
              break;
            }
            if ((isHigh && allLevels[k].level >= threshold) || (!isHigh && allLevels[k].level <= threshold)) {
              if (k > i + 1) {
                currentLevel.touches++;
              }
            }
          }

          // Сохраняем только ИНДЕКС уровня
          filteredIdxs.push(currentLevel.idx);
          processedIdxs.add(currentLevel.idx);
        }
      }
    }

    // Обновляем состояние длины
    if (isHigh) {
      this.lastHighLevelsLength = allLevels.length;
    } else {
      this.lastLowLevelsLength = allLevels.length;
    }
  }
}

export function findKeyLevels(candles, minutesInCandle, atr, initAtr) {
  let highLevels = [];
  let lowLevels = [];
  let highStack = [];
  let lowStack = [];

  for (let i = 0; i < candles.length; i++) {
    const candle = candles[i]
    const candleAtr = atr.nextValue(candle.open, candle.close, candle.high, candle.low) || initAtr

    while (highStack.length && candles[i].high > highLevels[highStack[highStack.length - 1]].level) {
      let idx = highStack.pop();
      highLevels[idx].duration = i - highLevels[idx].idx;
    }
    highStack.push(i);
    highLevels.push({ level: candles[i].high, duration: 0, minutesInCandle, startTime: candles[i].time, idx: i, atr: candleAtr });

    while (lowStack.length && candles[i].low < lowLevels[lowStack[lowStack.length - 1]].level) {
      let idx = lowStack.pop();
      lowLevels[idx].duration = i - lowLevels[idx].idx;
    }
    lowStack.push(i);
    lowLevels.push({ level: candles[i].low, duration: 0, minutesInCandle, startTime: candles[i].time, idx: i, atr: candleAtr });
  }

  // console.log('total candles', candles.length)
  while (highStack.length) {
    const idx = highStack.pop();
    let duration = candles.length - highLevels[idx].idx;
    highLevels[idx].duration = duration;
    highLevels[idx].unbeat = true;
    // console.log('unbeat high', idx, highLevels[idx].level, highLevels[idx].duration)
  }

  while (lowStack.length) {
    let idx = lowStack.pop();
    let duration = candles.length - lowLevels[idx].idx;
    lowLevels[idx].duration = duration;
    lowLevels[idx].unbeat = true;
    // console.log('unbeat low', idx, lowLevels[idx].level, lowLevels[idx].duration)
  }

  return { highLevels, lowLevels };
}

export function filterKeyLevels({ highLevels, lowLevels }, windowSize, touchesAtrMult) {
  const filterLevels = (levels, isHigh) => {
    let filteredLevels = [];

    for (let i = windowSize; i < levels.length; i++) {
      let isExtremum = true;
      for (let j = 1; j <= windowSize; j++) {
        if ((isHigh && levels[i - j].level >= levels[i].level) || (!isHigh && levels[i - j].level <= levels[i].level)) {
          isExtremum = false;
          break;
        }
      }

      if (isExtremum && levels[i].duration > 1) {
        levels[i].touches = 1; // Начальное касание уровня
        const delta = levels[i]?.atr * touchesAtrMult || 0
        const threshold = isHigh ? levels[i].level - delta : levels[i].level + delta;

        // Подсчет касаний с момента создания уровня
        for (let k = i + 1; k < levels.length; k++) {
          if ((isHigh && levels[k].level > levels[i].level) || (!isHigh && levels[k].level < levels[i].level)) {
            break; // Прерываем подсчет, если уровень пробит
          } else if ((isHigh && levels[k].level >= threshold) || (!isHigh && levels[k].level <= threshold)) {
            if (k > i + 1) levels[i].touches++;
          }
        }

        filteredLevels.push(levels[i]);
      }
    }

    return filteredLevels;
  };

  return {
    highLevels: filterLevels(highLevels, true),
    lowLevels: filterLevels(lowLevels, false)
  };
}

export function filterKeyLevels2({ highLevels, lowLevels }, minWindowSize, maxWindowSize, atrMult, percentage, maxIdx, useLast) {
  const importantHighLevels = filterImportantLevels(highLevels, minWindowSize, maxWindowSize);
  const importantLowLevels = filterImportantLevels(lowLevels, minWindowSize, maxWindowSize);

  const highLevels2 = importantHighLevels.map((level) => ({ ...level, type: 'resistance' }));
  const lowLevels2 = importantLowLevels.map((level) => ({ ...level, type: 'support' }));

  const allLevels = [...highLevels2, ...lowLevels2];

  // console.log('allLevels', allLevels);

  return findSimilarLevels(allLevels, atrMult, percentage, useLast);
}


export function filterImportantLevels(levels, minWindowSize, maxWindowSize) {
  const beatenLevels = levels.filter(lvl => !lvl.unbeat).length;
  let avgDuration = levels.reduce((acc, level) => acc + (level.unbeat ? 0 : level.duration), 0) / beatenLevels;
  avgDuration = Math.min(avgDuration, maxWindowSize);
  avgDuration = Math.max(avgDuration, minWindowSize);
  return levels.filter(lvl => (lvl.unbeat && lvl.duration > minWindowSize) || lvl.duration > avgDuration);
}

export function findSimilarLevels(levels, atrMult, percentage, useLast) {
  levels.sort((a, b) => a.level - b.level);
  // Initialize lowerBound and upperBound for each level
  levels.forEach(level => {
    const lowerBound = level.level - level.atr * atrMult || level.level * (1 - percentage);
    const upperBound = level.level + level.atr * atrMult || level.level * (1 + percentage);

    level.lowerBound = lowerBound // level.level * (1 - percentage);
    level.upperBound = upperBound // level.level * (1 + percentage);
  });

  let merged = true;

  while (merged) {
    merged = false;
    const result = [];
    for (let i = 0; i < levels.length - 1; i++) {
      const current = levels[i];
      const next = levels[i + 1];

      if (Math.abs(current.level - next.level) / current.level <= percentage) {
        const mergedLevel = mergeLevels(current, next, useLast);

        result.push(mergedLevel);
        i++;
        merged = true;  // Signal that a merge occurred
      } else {
        result.push(current);
      }
    }

    // Handle the last level separately
    if (levels.length > 0) {
      const last = levels[levels.length - 1];
      if (result.length > 0 && Math.abs(last.level - result[result.length - 1].level) / last.level <= percentage) {
        const mergedLevel = mergeLevels(result.pop(), last, useLast);

        result.push(mergedLevel);
        merged = true;  // Signal that a merge occurred
      } else {
        result.push(last);
      }
    }

    levels = result;  // Update levels for the next round
  }

  return levels;
}

export function mergeLevels(current, next, useLast) {
  const isBeatenByNext = current.idx + current.duration === next.idx;
  const isBeatenByCurrent = next.idx + next.duration === current.idx;
  const mergeTouches = isBeatenByNext || isBeatenByCurrent;

  const currentTouches = (!current.unbeat && !mergeTouches)
    ? 0 : current.touches ? current.touches : 1;
  const nextTouches = (!next.unbeat && !mergeTouches)
    ? 0 : next.touches ? next.touches : 1;

  let last = next
  if (next.idx < current.idx) {
    last = current
  }
  const lastTouches = (!last.unbeat && !mergeTouches)
    ? 0 : last.touches ? last.touches : 1;

  const mergedLevels = [...(current.mergedLevels || [current]), ...(next.mergedLevels || [next])]
  mergedLevels.sort((a, b) => a.startTime - b.startTime)
  last = mergedLevels[mergedLevels.length - 1]

  let mergedLevel
  if (useLast) {
    mergedLevel = {
      level: last.level,
      lastLevel: last.lastLevel || last.level,
      duration: current.lastDuration || current.duration + next.duration || next.lastDuration,
      lastDuration: last.lastDuration || last.duration,
      startTime: last.startTime,
      touches: lastTouches,
      mergedTouches: currentTouches + nextTouches,
      lowerBound: last.lowerBound,
      upperBound: last.upperBound,
      mergedLevels,
    }
  } else {
    mergedLevel = {
      level: (current.level + next.level) / 2,
      lastLevel: last.lastLevel || last.level,
      duration: current.lastDuration || current.duration + next.duration || next.lastDuration,
      lastDuration: last.lastDuration || last.duration,
      startTime: Math.min(current.startTime, next.startTime),
      touches: lastTouches,
      mergedTouches: currentTouches + nextTouches,
      lowerBound: Math.min(current.lowerBound, next.lowerBound),
      upperBound: Math.max(current.upperBound, next.upperBound),
      mergedLevels,
    }
  }

  if (current.unbeat || next.unbeat) mergedLevel.unbeat = true;
  if (current.type === next.type) {
    mergedLevel.type = current.type;
  } else {
    mergedLevel.type = last.type
    mergedLevel.mtype = 'double';
  }
  // console.log('mergeLevels', current, next, mergedLevel);
  return mergedLevel;
}

// fibo 

export function getFibonacciLevels(levels) {
  const fibonacciLevels = []
  const fibLevels = [0.236, 0.382, 0.5, 0.618, 0.786, 1.618] // 0.236, 0.786, 1.618, 2, 2.618, 4.236]

  for (let i = 0; i < levels.length; i++) {
    const lvl1 = levels[i]
    const price1 = lvl1.level
    const type1 = lvl1.type

    for (let j = i + 1; j < levels.length; j++) {
      const lvl2 = levels[j]
      const price2 = lvl2.level
      const type2 = lvl2.type
      if (price1 === price2) {
        // console.log('price1 === price2', price1, price2)
        continue
      }
      if (type1 === type2 && (type1 !== "double" && type2 !== "double")) {
        // console.log('type1 === type2', type1, type2)
        continue
      }

      const priceDiff = Math.abs(price1 - price2)
      const minPrice = Math.min(price1, price2)
      const maxPrice = Math.max(price1, price2)

      // для нисходящего тренда
      // Уровень Фибоначчи = минимальная цена + (максимальная цена ーминимальная цена) * уровень Фибоначчи
      // для восходящего тренда
      // Уровень Фибоначчи = максимальная цена ー (максимальная цена ーминимальная цена) * уровень Фибоначчи
      for (const mult of fibLevels) {
        const levelUp = { level: minPrice + priceDiff * mult, type: "fibo", mult, lvl1: lvl1.level, lvl2: lvl2.level }
        const levelDn = { level: maxPrice - priceDiff * mult, type: "fibo", mult, lvl1: lvl1.level, lvl2: lvl2.level }
        fibonacciLevels.push(levelUp)
        fibonacciLevels.push(levelDn)
      }
    }
  }

  const filtered = []
  // remove duplicates
  fibonacciLevels.forEach(lvl1 => {
    if (!filtered.some(lvl2 => lvl1.lvl1 === lvl2.lvl1 && lvl1.lvl2 === lvl2.lvl2 && lvl1.level === lvl2.level)) {
      filtered.push(lvl1);
    }
  });

  // fibonacciLevels.sort((a, b) => a.level - b.level)
  // filtered.sort((a, b) => a.level - b.level)

  // console.log('fibonacciLevels filtered', filtered)

  const similar = findSimilarFiboLevels(filtered, similarLevelPercent, true)

  // console.log('fibonacciLevels similar', similar)

  return similar
}

export function findSimilarFiboLevels(levels, percentage) {
  levels.sort((a, b) => a.level - b.level);
  // Initialize lowerBound and upperBound for each level
  levels.forEach(level => {
    level.lowerBound = level.level * (1 - percentage);
    level.upperBound = level.level * (1 + percentage);
  });

  let merged = true;

  while (merged) {
    merged = false;
    const result = [];
    for (let i = 0; i < levels.length - 1; i++) {
      const current = levels[i];
      const next = levels[i + 1];

      if (Math.abs(current.level - next.level) / current.level <= percentage) {
        const mergedLevel = mergeFibo(current, next)

        result.push(mergedLevel);
        i++;
        merged = true;  // Signal that a merge occurred
      } else {
        result.push(current);
      }
    }

    // Handle the last level separately
    if (levels.length > 0) {
      const last = levels[levels.length - 1];
      if (result.length > 0 && Math.abs(last.level - result[result.length - 1].level) / last.level <= percentage) {
        const mergedLevel = mergeFibo(result.pop(), last)

        result.push(mergedLevel);
        merged = true;  // Signal that a merge occurred
      } else {
        result.push(last);
      }
    }

    levels = result;  // Update levels for the next round
  }

  return levels;
}

export function mergeFibo(current, next) {
  const mergedLevels = [...(current.mergedLevels || [current]), ...(next.mergedLevels || [next])]
  mergedLevels.sort((a, b) => a.startTime - b.startTime)

  const mergedLevel = {
    type: 'fibo',
    level: (current.level + next.level) / 2,
    lowerBound: Math.min(current.lowerBound, next.lowerBound),
    upperBound: Math.max(current.upperBound, next.upperBound),
    mergedLevels,
  };

  return mergedLevel;
}


// **********************************
//  Wave Stats
// **********************************

// ***********************************************
// Statistics & Utils
// ***********************************************


const url = new URL('./backtestWorker.js', import.meta.url);
const backtestWorker = new Worker(url, { type: "module" })
backtestWorker.onerror = (err) => {
  console.error('backtest worker onerror', err);
};
backtestWorker.onmessage = (e) => {
  const message = e.data;
  console.log('backtest worker onmessage', message)
};

function work(worker, message) {
  return new Promise((resolve, reject) => {
    const listener = (event) => {
      worker.removeEventListener('message', listener);
      resolve(event.data);
    };
    worker.addEventListener('message', listener);
    worker.postMessage(message);
  });
}

export async function waveBacktestWorker(props) {
  const result = await work(backtestWorker, 'stats.reset');
  return result
}


export class WaveStatsManagerWorker {
  constructor(id) {
    this.totalWaves = 0;
    this.id = id
  }

  async addWaves(newWaves) {
    this.totalWaves += newWaves.length

    const result = await work(backtestWorker, {
      action: 'stats.addWaves',
      window: this.id,
      newWaves
    });
    // console.log('addWaves', result)

    this.totalWaves = result.totalWaves

    return result.success
  }

  async getStats() {
    const result = await work(backtestWorker, {
      action: 'stats.getStats',
      window: this.id
    });

    // console.log('getStats', result)

    return result.stats
  }

  // Метод для сброса, если потребуется
  async reset() {
    const result = await work(backtestWorker, {
      action: 'stats.reset',
      window: this.id,
    });
    // console.log('statsResetResult', result)

    if (result.success) {
      this.totalWaves = 0;
    }
    return result.success
  }
}


function deepCopyStatsData(source) {
  const target = {};
  for (const catKey in source) {
    if (!source[catKey]) continue;

    if (catKey === 'sequences') {
      target.sequences = {};
      for (const seqKey in source.sequences) {
        const sourceSeq = source.sequences[seqKey];
        target.sequences[seqKey] = { up: {}, down: {} };
        if (sourceSeq.up) {
          for (const metricKey in sourceSeq.up) {
            target.sequences[seqKey].up[metricKey] = [...sourceSeq.up[metricKey]];
          }
        }
        if (sourceSeq.down) {
          for (const metricKey in sourceSeq.down) {
            target.sequences[seqKey].down[metricKey] = [...sourceSeq.down[metricKey]];
          }
        }
      }
    } else {
      target[catKey] = {};
      const sourceCategory = source[catKey];
      for (const metricKey in sourceCategory) {
        if (Array.isArray(sourceCategory[metricKey])) {
          target[catKey][metricKey] = [...sourceCategory[metricKey]];
        }
      }
    }
  }
  return target;
}

// Вспомогательная функция для создания пустого контейнера
function createStatsDataContainer() {
  return {
    deltaPercent: [],
    deltaRatioPrev: [],
    totalVolume: [],
    effortRatio: [],
    // maxDiff: [],
    maxDiffPercent: [],
    // maxDiffUp: [],
    maxDiffUpPercent: [],
    // maxDiffDn: [],
    maxDiffDnPercent: [],
    duration: [],
    delta: [],
  };
}

export class WaveStatsManager {
  constructor() {
    this.processedWaves = [];
    this.statsDataSnapshots = [];
    this.finalStats = {};
  }

  update(currentWaves) {
    let anchorIndexInOld = -1;
    let anchorIndexInNew = -1;

    let i = this.processedWaves.length - 1;
    let j = currentWaves.length - 1;

    // Шаг 1: Эффективный поиск "якоря" с конца
    while (i >= 0 && j >= 0) {
      const oldWave = this.processedWaves[i];
      const newWave = currentWaves[j];

      if (oldWave.start > newWave.start) {
        i--;
      } else if (newWave.start > oldWave.start) {
        j--;
      } else {
        if (this._wavesAreEqual(oldWave, newWave)) {
          // Нашли якорь!
          anchorIndexInOld = i;
          anchorIndexInNew = j;
          break; // Поиск завершен
        } else {
          // start одинаковый, но волны разные (изменились end или type)
          i--;
          j--;
        }
      }
    }

    // Индекс, с которого начинаются изменения в *новом* массиве
    const firstChangedIndexInNew = anchorIndexInNew + 1;

    // --- Логика отката к снэпшоту ---
    // Нам нужно найти снэпшот, соответствующий нашему якорю
    const lastGoodSnapshot = anchorIndexInOld !== -1
      ? this.statsDataSnapshots[anchorIndexInOld]
      : null;

    let currentStatsData = lastGoodSnapshot
      ? deepCopyStatsData(lastGoodSnapshot)
      : this._getInitialStatsData();

    // Обрезаем массив снэпшотов до якоря
    this.statsDataSnapshots.length = anchorIndexInOld + 1;

    // Берем для обработки только ту часть нового массива, что идет после якоря
    const wavesToProcess = currentWaves.slice(firstChangedIndexInNew);

    // Передаем правильный стартовый индекс для allWaves
    this._processWavesIncrementally(currentWaves, firstChangedIndexInNew, currentStatsData, wavesToProcess);

    // Сохраняем новое состояние
    this.processedWaves = [...currentWaves];
    this.finalStats = this._calculateFinalStats(currentStatsData, this.processedWaves.length);
  }

  // Good old update
  update2(currentWaves) {
    let firstChangedIndex = 0;
    const minLength = Math.min(this.processedWaves.length, currentWaves.length);

    if (this.processedWaves.length > 0) {
      for (let i = 0; i < minLength; i++) {
        const oldWave = this.processedWaves[i];
        const newWave = currentWaves[i];
        if (oldWave.start !== newWave.start || oldWave.end !== newWave.end || oldWave.delta !== newWave.delta) {
          break;
        }
        firstChangedIndex = i + 1;
      }
      if (this.processedWaves.length !== currentWaves.length && firstChangedIndex === minLength) {
        firstChangedIndex = minLength;
      }
    }

    const lastGoodSnapshot = firstChangedIndex > 0
      ? this.statsDataSnapshots[firstChangedIndex - 1]
      : null;

    let currentStatsData = lastGoodSnapshot
      ? deepCopyStatsData(lastGoodSnapshot)
      : this._getInitialStatsData()

    this.statsDataSnapshots.length = firstChangedIndex;
    const wavesToProcess = currentWaves.slice(firstChangedIndex);

    this._processWavesIncrementally(currentWaves, firstChangedIndex, currentStatsData, wavesToProcess);

    this.processedWaves = [...currentWaves];
    this.finalStats = this._calculateFinalStats(currentStatsData, currentWaves.length);
  }

  _wavesAreEqual(oldWave, newWave) {
    // Если один из объектов не существует (например, при разной длине массивов), они не равны.
    if (!oldWave || !newWave) {
      return false;
    }

    return oldWave.start === newWave.start &&
      oldWave.end === newWave.end &&
      oldWave.delta === newWave.delta;
  }

  _getInitialStatsData() {
    return {
      overall: createStatsDataContainer(),
      up: createStatsDataContainer(),
      down: createStatsDataContainer(),
      move: createStatsDataContainer(),
      pullback: createStatsDataContainer(),
      moveUp: createStatsDataContainer(),
      moveDown: createStatsDataContainer(),
      pullbackUp: createStatsDataContainer(),
      pullbackDown: createStatsDataContainer(),
      sequences: {
        pullbackPreMove: { up: createStatsDataContainer(), down: createStatsDataContainer() },
        pullbackPrePullback: { up: createStatsDataContainer(), down: createStatsDataContainer() },
        movePreMove: { up: createStatsDataContainer(), down: createStatsDataContainer() },
        movePrePullback: { up: createStatsDataContainer(), down: createStatsDataContainer() },
        movePostPullback: { up: createStatsDataContainer(), down: createStatsDataContainer() },
        pullbackPostPullback: { up: createStatsDataContainer(), down: createStatsDataContainer() },
        pullbackPostMove: { up: createStatsDataContainer(), down: createStatsDataContainer() },
        movePostMove: { up: createStatsDataContainer(), down: createStatsDataContainer() },
      }
    };
  }

  _processWavesIncrementally(allWaves, startIndex, statsData, wavesToAdd) {
    const waveDurationMult = 60000;

    for (let i = 0; i < wavesToAdd.length; i++) {
      const currentWave = wavesToAdd[i];
      const previousWave = (startIndex + i === 0) ? null : allWaves[startIndex + i - 1];

      // --- Сбор статистики по последовательностям ---
      if (previousWave) {
        const prevType = !previousWave.type || previousWave.type === 'move' ? 'move' : 'pullback';
        const currentType = !currentWave.type || currentWave.type === 'move' ? 'move' : 'pullback';

        // Логика 1: "Pre" статистика (описываем PREVIOUS wave)
        let preContainer = null;
        if (prevType === 'pullback' && currentType === 'move') preContainer = statsData.sequences.pullbackPreMove;
        else if (prevType === 'pullback' && currentType === 'pullback') preContainer = statsData.sequences.pullbackPrePullback;
        else if (prevType === 'move' && currentType === 'move') preContainer = statsData.sequences.movePreMove;
        else if (prevType === 'move' && currentType === 'pullback') preContainer = statsData.sequences.movePrePullback;

        if (preContainer) {
          const subCategory = currentWave.direction === 'up' ? preContainer.up : preContainer.down;
          const { delta, deltaPercent, maxDiff, maxDiffPercent, maxDiffUp, maxDiffUpPercent, maxDiffDn, maxDiffDnPercent, totalVolume } = previousWave;
          const waveBeforePrevious = (startIndex + i > 1) ? allWaves[startIndex + i - 2] : null;

          subCategory.deltaPercent.push(deltaPercent);
          subCategory.deltaRatioPrev.push((waveBeforePrevious && waveBeforePrevious.deltaPercent !== 0) ? previousWave.deltaPercent / waveBeforePrevious.deltaPercent : 0);
          subCategory.totalVolume.push(totalVolume);
          subCategory.effortRatio.push(previousWave.delta !== 0 ? totalVolume / Math.abs(previousWave.deltaPercent) : 0);
          // subCategory.maxDiff.push(maxDiff);
          subCategory.maxDiffPercent.push(maxDiffPercent);
          // subCategory.maxDiffUp.push(maxDiffUp);
          subCategory.maxDiffUpPercent.push(maxDiffUpPercent);
          // subCategory.maxDiffDn.push(maxDiffDn);
          subCategory.maxDiffDnPercent.push(maxDiffDnPercent);
          subCategory.duration.push((previousWave.end - previousWave.start) / waveDurationMult);
          subCategory.delta.push(delta);
        }

        // Логика 2: "Post" статистика (описываем CURRENT wave)
        let postContainer = null;
        if (currentType === 'move' && prevType === 'pullback') postContainer = statsData.sequences.movePostPullback;
        else if (currentType === 'pullback' && prevType === 'pullback') postContainer = statsData.sequences.pullbackPostPullback;
        else if (currentType === 'pullback' && prevType === 'move') postContainer = statsData.sequences.pullbackPostMove;
        else if (currentType === 'move' && prevType === 'move') postContainer = statsData.sequences.movePostMove;

        if (postContainer) {
          const subCategory = currentWave.direction === 'up' ? postContainer.up : postContainer.down;
          const { delta, deltaPercent, maxDiff, maxDiffPercent, maxDiffUp, maxDiffUpPercent, maxDiffDn, maxDiffDnPercent, totalVolume } = currentWave;

          subCategory.deltaPercent.push(deltaPercent);
          subCategory.deltaRatioPrev.push((previousWave && previousWave.deltaPercent !== 0) ? deltaPercent / previousWave.deltaPercent : 0);
          subCategory.totalVolume.push(totalVolume);
          subCategory.effortRatio.push(delta !== 0 ? totalVolume / Math.abs(deltaPercent) : 0);
          // subCategory.maxDiff.push(maxDiff);
          subCategory.maxDiffPercent.push(maxDiffPercent);
          // subCategory.maxDiffUp.push(maxDiffUp);
          subCategory.maxDiffUpPercent.push(maxDiffUpPercent);
          // subCategory.maxDiffDn.push(maxDiffDn);
          subCategory.maxDiffDnPercent.push(maxDiffDnPercent);
          subCategory.duration.push((currentWave.end - currentWave.start) / waveDurationMult);
          subCategory.delta.push(delta);
        }
      }

      // --- Базовая классификация текущей волны ---
      const {
        delta, deltaPercent,
        // maxDiff,
        maxDiffPercent,
        // maxDiffUp,
        maxDiffUpPercent,
        // maxDiffDn,
        maxDiffDnPercent,
        totalVolume
      } = currentWave;

      const pushToCategory = (category) => {
        category.deltaPercent.push(deltaPercent);
        category.deltaRatioPrev.push((previousWave && previousWave.deltaPercent !== 0) ? deltaPercent / previousWave.deltaPercent : 0);
        category.totalVolume.push(totalVolume);
        category.effortRatio.push(delta !== 0 ? totalVolume / Math.abs(deltaPercent) : 0);
        // category.maxDiff.push(maxDiff);
        category.maxDiffPercent.push(maxDiffPercent);
        // category.maxDiffUp.push(maxDiffUp);
        category.maxDiffUpPercent.push(maxDiffUpPercent);
        // category.maxDiffDn.push(maxDiffDn);
        category.maxDiffDnPercent.push(maxDiffDnPercent);
        category.duration.push((currentWave.end - currentWave.start) / waveDurationMult);
        category.delta.push(delta);
      };

      const isMove = !currentWave.type || currentWave.type === 'move';
      const isPullback = currentWave.type === 'pullback';
      const isUp = currentWave.direction === 'up';
      const isDown = currentWave.direction === 'down';

      pushToCategory(statsData.overall);
      if (isUp) pushToCategory(statsData.up);
      if (isDown) pushToCategory(statsData.down);
      if (isMove) pushToCategory(statsData.move);
      if (isPullback) pushToCategory(statsData.pullback);
      if (isMove && isUp) pushToCategory(statsData.moveUp);
      if (isMove && isDown) pushToCategory(statsData.moveDown);
      if (isPullback && isUp) pushToCategory(statsData.pullbackUp);
      if (isPullback && isDown) pushToCategory(statsData.pullbackDown);

      this.statsDataSnapshots[startIndex + i] = deepCopyStatsData(statsData);
    }
  }

  _calculateFinalStats(statsData, totalWaves) {
    const s = statsData;
    const buildStatsForMetric = (values) => {
      const baseStats = calculateStatistics(values, true);
      const distribution = analyzeDistribution(values, baseStats);
      return { ...baseStats, distribution };
    };

    const buildStatsForCategory = (category) => {
      if (!category || !category.duration) return { count: 0 };
      return {
        count: category.duration.length,
        deltaPercent: buildStatsForMetric(category.deltaPercent),
        deltaRatioPrev: buildStatsForMetric(category.deltaRatioPrev),
        totalVolume: buildStatsForMetric(category.totalVolume),
        effortRatio: buildStatsForMetric(category.effortRatio),
        // maxDiff: buildStatsForMetric(category.maxDiff),
        maxDiffPercent: buildStatsForMetric(category.maxDiffPercent),
        // maxDiffUp: buildStatsForMetric(category.maxDiffUp),
        maxDiffUpPercent: buildStatsForMetric(category.maxDiffUpPercent),
        // maxDiffDn: buildStatsForMetric(category.maxDiffDn),
        maxDiffDnPercent: buildStatsForMetric(category.maxDiffDnPercent),
        duration: buildStatsForMetric(category.duration),
        delta: buildStatsForMetric(category.delta),
      };
    };

    const buildSequenceCategory = (seqCategory) => {
      if (!seqCategory) return { count: 0, up: { count: 0 }, down: { count: 0 } };

      const combinedData = createStatsDataContainer();
      for (const metricKey in combinedData) {
        combinedData[metricKey] = [
          ...(seqCategory.up?.[metricKey] || []),
          ...(seqCategory.down?.[metricKey] || [])
        ];
      }

      const overallStats = buildStatsForCategory(combinedData);
      return {
        ...overallStats,
        up: buildStatsForCategory(seqCategory.up),
        down: buildStatsForCategory(seqCategory.down)
      };
    };

    return {
      overall: {
        ...buildStatsForCategory(s.overall),
        up: buildStatsForCategory(s.up),
        down: buildStatsForCategory(s.down)
      },
      move: {
        ...buildStatsForCategory(s.move),
        up: buildStatsForCategory(s.moveUp),
        down: buildStatsForCategory(s.moveDown)
      },
      pullback: {
        ...buildStatsForCategory(s.pullback),
        up: buildStatsForCategory(s.pullbackUp),
        down: buildStatsForCategory(s.pullbackDown)
      },
      sequences: {
        pullbackPreMove: buildSequenceCategory(s.sequences.pullbackPreMove),
        pullbackPrePullback: buildSequenceCategory(s.sequences.pullbackPrePullback),
        movePreMove: buildSequenceCategory(s.sequences.movePreMove),
        movePrePullback: buildSequenceCategory(s.sequences.movePrePullback),
        movePostPullback: buildSequenceCategory(s.sequences.movePostPullback),
        pullbackPostPullback: buildSequenceCategory(s.sequences.pullbackPostPullback),
        pullbackPostMove: buildSequenceCategory(s.sequences.pullbackPostMove),
        movePostMove: buildSequenceCategory(s.sequences.movePostMove),
      }
    };
  }

  reset() {
    this.processedWaves = [];
    this.statsDataSnapshots = [];
    this.finalStats = {};
  }

  getStats() {
    return this.finalStats;
  }
}

/**
 * Анализирует распределение данных.
 * @param {Array<number>} values - Массив с числовыми данными.
 * @param {object} stats - Объект с уже посчитанной статистикой (avg, stdDev, median, mad, count).
 * @returns {object} - Объект с массивами процентных распределений.
 */
export function analyzeDistribution(values, stats) {
  const { avg, stdDev, median, mad, count } = stats;

  if (count === 0) {
    return {
      stdDev: [0, 0, 0, 0],
      mad: [0, 0, 0, 0],
      stdDevNeg: [0, 0, 0, 0],
      madNeg: [0, 0, 0, 0],
      stdDevPos: [0, 0, 0, 0],
      madPos: [0, 0, 0, 0],
    };
  }

  // Инициализируем все счетчики
  const stdDevCounts = [0, 0, 0, 0];
  const madCounts = [0, 0, 0, 0];
  const stdDevNegCounts = [0, 0, 0, 0];
  const madNegCounts = [0, 0, 0, 0];
  const stdDevPosCounts = [0, 0, 0, 0];
  const madPosCounts = [0, 0, 0, 0];

  for (const value of values) {
    if (value <= avg) {
      stdDevCounts[0]++;
      stdDevPosCounts[0]++;
      stdDevNegCounts[0]++;
    }
    if (value <= median) {
      madCounts[0]++;
      madNegCounts[0]++;
      madPosCounts[0]++;
    }

    if (value <= avg + stdDev * 1) stdDevPosCounts[1]++;
    if (value <= avg + stdDev * 2) stdDevPosCounts[2]++;
    if (value <= avg + stdDev * 3) stdDevPosCounts[3]++;

    if (value <= median + mad * 1) madPosCounts[1]++;
    if (value <= median + mad * 2) madPosCounts[2]++;
    if (value <= median + mad * 3) madPosCounts[3]++;

    if (value <= avg - stdDev * 1) stdDevNegCounts[1]++;
    if (value <= avg - stdDev * 2) stdDevNegCounts[2]++;
    if (value <= avg - stdDev * 3) stdDevNegCounts[3]++;

    if (value <= median - mad * 1) madNegCounts[1]++;
    if (value <= median - mad * 2) madNegCounts[2]++;
    if (value <= median - mad * 3) madNegCounts[3]++;

    if (stdDev > 0) {
      if (Math.abs(value - avg) <= stdDev * 1) stdDevCounts[1]++;
      if (Math.abs(value - avg) <= stdDev * 2) stdDevCounts[2]++;
      if (Math.abs(value - avg) <= stdDev * 3) stdDevCounts[3]++;
    }
    if (mad > 0) {
      if (Math.abs(value - median) <= mad * 1) madCounts[1]++;
      if (Math.abs(value - median) <= mad * 2) madCounts[2]++;
      if (Math.abs(value - median) <= mad * 3) madCounts[3]++;
    }
  }

  // Конвертируем счетчики в дроби
  const stdDevPercents = stdDevCounts.map(c => (c / count));
  const madPercents = madCounts.map(c => (c / count));
  const stdDevNegPercents = stdDevNegCounts.map(c => (c / count));
  const madNegPercents = madNegCounts.map(c => (c / count));
  const stdDevPosPercents = stdDevPosCounts.map(c => (c / count));
  const madPosPercents = madPosCounts.map(c => (c / count));

  return {
    stdDev: stdDevPercents,
    mad: madPercents,
    // Негативный хвост
    stdDevNeg: stdDevNegPercents,
    madNeg: madNegPercents,
    // Позитивный хвост
    stdDevPos: stdDevPosPercents,
    madPos: madPosPercents,
  };
}

export function calculateStatistics(v, useEMA = false, period = simpleStatsEmaLength, field) {
  // Проверка на пустой массив и подготовка данных (без изменений)
  if (!v?.length) return { avg: 0, median: 0, min: 0, max: 0, stdDev: 0, slope: 0, intercept: 0, mad: 0 }; // +
  const values = field ? v.map(val => val[field] || 0) : v.map(val => val || 0);

  // Расчет среднего и линейной регрессии (без изменений)
  const sum = values.reduce((acc, val) => acc + val, 0);
  const avg = sum / values.length;
  const xBar = (values.length - 1) / 2;
  let num = 0;
  let den = 0;
  values.forEach((y, x) => {
    num += (x - xBar) * (y - avg);
    den += (x - xBar) * (x - xBar);
  });
  const slope = den === 0 ? 0 : num / den;
  const intercept = avg - slope * xBar;

  // EMA (без изменений)
  let emaValues, emaStats;
  if (useEMA) {
    emaValues = calculateEMA(values, period);
    emaStats = calculateStatistics(emaValues, false); // Рекурсивный вызов без EMA
  }

  // Сортировка и базовые метрики (без изменений)
  values.sort((a, b) => a - b);
  const median = values.length % 2 === 0
    ? (values[values.length / 2 - 1] + values[values.length / 2]) / 2
    : values[Math.floor(values.length / 2)];
  const min = values[0];
  const max = values[values.length - 1];

  // Стандартное отклонение (без изменений)
  const variance = values.reduce((acc, val) => acc + Math.pow(val - avg, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);

  // --- РАСЧЕТ MAD ---
  // + 1. Находим массив абсолютных отклонений от медианы.
  const deviations = values.map(val => Math.abs(val - median));

  // + 2. Сортируем отклонения, чтобы найти их медиану.
  deviations.sort((a, b) => a - b);

  // + 3. Вычисляем медиану отклонений (это и есть MAD).
  const mad = deviations.length % 2 === 0
    ? (deviations[deviations.length / 2 - 1] + deviations[deviations.length / 2]) / 2
    : deviations[Math.floor(deviations.length / 2)];
  // --- КОНЕЦ БЛОКА MAD ---


  // Возвращаем результат
  if (useEMA) {
    return {
      count: values.length,
      avg, median,
      cv: stdDev / avg,
      range: max - min,
      stdDev,
      mad,
      min, max,
      slope, intercept,
      ema: emaStats
    };
  } else {
    return {
      count: values.length,
      avg, median,
      cv: stdDev / avg,
      range: max - min,
      stdDev,
      mad,
      min, max,
      slope, intercept
    };
  }
}

export function calculateStatistics2(v, useEMA = false, period = simpleStatsEmaLength) {
  if (!v.length) return { avg: 0, median: 0, min: 0, max: 0, stdDev: 0, slope: 0, intercept: 0 };

  const values = v.map(val => val || 0);

  const sum = values.reduce((acc, val) => acc + val, 0);
  const avg = sum / values.length;

  // Линейная регрессия
  const xBar = (values.length - 1) / 2;
  let num = 0;
  let den = 0;
  values.forEach((y, x) => {
    num += (x - xBar) * (y - avg);
    den += (x - xBar) * (x - xBar);
  });

  const slope = num / den;
  const intercept = avg - slope * xBar;

  // ema stats
  let emaValues, emaStats
  if (useEMA) {
    emaValues = calculateEMA(values, period);
    emaStats = calculateStatistics(emaValues);
  }

  // median
  values.sort((a, b) => a - b);
  const median = values.length % 2 === 0 ? (values[values.length / 2 - 1] + values[values.length / 2]) / 2 : values[Math.floor(values.length / 2)];
  // extrems
  const min = values[0];
  const max = values[values.length - 1];

  // stdDev
  const variance = values.reduce((acc, val) => acc + Math.pow(val - avg, 2), 0) / values.length;
  const stdDev = Math.sqrt(variance);

  if (useEMA) {
    return {
      count: values.length,
      avg, median,
      min, max,
      stdDev,
      slope, intercept,
      ema: emaStats
    };
  } else {
    return {
      count: values.length,
      avg, median,
      min, max,
      stdDev,
      slope, intercept
    };
  }
}

export function getSimpleWaveStats(waves) {
  const moveWaves = waves.filter(wave => !wave.type || wave.type === 'move')
  const pullbackWaves = waves.filter(wave => wave.type === 'pullback')

  const upWaves = waves.filter(wave => wave.direction === 'up')
  const downWaves = waves.filter(wave => wave.direction === 'down')

  const moveUpWaves = moveWaves.filter(wave => wave.direction === 'up')
  const moveDownWaves = moveWaves.filter(wave => wave.direction === 'down')
  const pullbackUpWaves = pullbackWaves.filter(wave => wave.direction === 'up')
  const pullbackDownWaves = pullbackWaves.filter(wave => wave.direction === 'down')

  const stats = {
    wavesNumber: waves.length,
    duration: waves.map(wave => (wave.end - wave.start) / waveDurationMult), // Продолжительность в минутах
    delta: waves.map(wave => wave.delta),
    deltaPercent: waves.map(wave => wave.deltaPercent),
    // 
    upWavesNumber: upWaves.length,
    upDuration: upWaves.map(wave => (wave.end - wave.start) / waveDurationMult),
    upDelta: upWaves.map(wave => wave.delta),
    upDeltaPercent: upWaves.map(wave => wave.deltaPercent),
    // 
    downWavesNumber: downWaves.length,
    downDuration: downWaves.map(wave => (wave.end - wave.start) / waveDurationMult),
    downDelta: downWaves.map(wave => wave.delta),
    downDeltaPercent: downWaves.map(wave => wave.deltaPercent),
    // 
    moveWavesNumber: moveWaves.length,
    moveDuration: moveWaves.map(wave => (wave.end - wave.start) / waveDurationMult),
    moveDelta: moveWaves.map(wave => wave.delta),
    moveDeltaPercent: moveWaves.map(wave => wave.deltaPercent),
    //
    pullbackWavesNumber: pullbackWaves.length,
    pullbackDuration: pullbackWaves.map(wave => (wave.end - wave.start) / waveDurationMult),
    pullbackDelta: pullbackWaves.map(wave => wave.delta),
    pullbackDeltaPercent: pullbackWaves.map(wave => wave.deltaPercent),
    //
    moveUpWavesNumber: moveUpWaves.length,
    moveUpDuration: moveUpWaves.map(wave => (wave.end - wave.start) / waveDurationMult),
    moveUpDelta: moveUpWaves.map(wave => wave.delta),
    moveUpDeltaPercent: moveUpWaves.map(wave => wave.deltaPercent),
    //
    moveDownWavesNumber: moveDownWaves.length,
    moveDownDuration: moveDownWaves.map(wave => (wave.end - wave.start) / waveDurationMult),
    moveDownDelta: moveDownWaves.map(wave => wave.delta),
    moveDownDeltaPercent: moveDownWaves.map(wave => wave.deltaPercent),
    //
    pullbackUpWavesNumber: pullbackUpWaves.length,
    pullbackUpDuration: pullbackUpWaves.map(wave => (wave.end - wave.start) / waveDurationMult),
    pullbackUpDelta: pullbackUpWaves.map(wave => wave.delta),
    pullbackUpDeltaPercent: pullbackUpWaves.map(wave => wave.deltaPercent),
    //
    pullbackDownWavesNumber: pullbackDownWaves.length,
    pullbackDownDuration: pullbackDownWaves.map(wave => (wave.end - wave.start) / waveDurationMult),
    pullbackDownDelta: pullbackDownWaves.map(wave => wave.delta),
    pullbackDownDeltaPercent: pullbackDownWaves.map(wave => wave.deltaPercent),
  };

  return {
    overall: {
      count: stats.wavesNumber,
      duration: calculateStatistics(stats.duration, true),
      delta: calculateStatistics(stats.delta, true),
      deltaPercent: calculateStatistics(stats.deltaPercent, true),
      up: {
        count: stats.upWavesNumber,
        duration: calculateStatistics(stats.upDuration, true),
        delta: calculateStatistics(stats.upDelta, true),
        deltaPercent: calculateStatistics(stats.upDeltaPercent, true),
      },
      down: {
        count: stats.downWavesNumber,
        duration: calculateStatistics(stats.downDuration, true),
        delta: calculateStatistics(stats.downDelta, true),
        deltaPercent: calculateStatistics(stats.downDeltaPercent, true),
      },
    },
    move: {
      count: stats.moveWavesNumber,
      duration: calculateStatistics(stats.moveDuration, true),
      delta: calculateStatistics(stats.moveDelta, true),
      deltaPercent: calculateStatistics(stats.moveDeltaPercent, true),
      up: {
        count: stats.moveUpWavesNumber,
        duration: calculateStatistics(stats.moveUpDuration, true),
        delta: calculateStatistics(stats.moveUpDelta, true),
        deltaPercent: calculateStatistics(stats.moveUpDeltaPercent, true),
      },
      down: {
        count: stats.moveDownWavesNumber,
        duration: calculateStatistics(stats.moveDownDuration, true),
        delta: calculateStatistics(stats.moveDownDelta, true),
        deltaPercent: calculateStatistics(stats.moveDownDeltaPercent, true),
      },
    },
    pullback: {
      count: stats.pullbackWavesNumber,
      duration: calculateStatistics(stats.pullbackDuration, true),
      delta: calculateStatistics(stats.pullbackDelta, true),
      deltaPercent: calculateStatistics(stats.pullbackDeltaPercent, true),
      up: {
        count: stats.pullbackUpWavesNumber,
        duration: calculateStatistics(stats.pullbackUpDuration, true),
        delta: calculateStatistics(stats.pullbackUpDelta, true),
        deltaPercent: calculateStatistics(stats.pullbackUpDeltaPercent, true),
      },
      down: {
        count: stats.pullbackDownWavesNumber,
        duration: calculateStatistics(stats.pullbackDownDuration, true),
        delta: calculateStatistics(stats.pullbackDownDelta, true),
        deltaPercent: calculateStatistics(stats.pullbackDownDeltaPercent, true),
      },
    },
  };

}

export function calculateEMA(values, period) {
  const smoothingFactor = 2 / (period + 1);
  const emaValues = [];
  let prevEMA = values[0]; // Начальное значение EMA равно первому значению

  for (let i = 1; i < values.length; i++) {
    const currentEMA = (values[i] * smoothingFactor) + (prevEMA * (1 - smoothingFactor));
    emaValues.push(currentEMA);
    prevEMA = currentEMA;
  }

  return emaValues;
}


export function getWaveContinuation(waves, stats, candles, prevWaveUpdated) {
  let hasNewCandles = false
  let highUpdated = false
  let lowUpdated = false
  let waveUpdated = false
  let isNewWave = false

  const newCandles = []
  if (waves.length < 2 || !candles.length) {
    return {
      hasNewCandles,
      highUpdated,
      lowUpdated,
      newCandles,
    }
  }

  const prevWave = waves[waves.length - 2]
  const lastWave = waves[waves.length - 1]

  let high = lastWave.high
  let low = lastWave.low
  let delta = lastWave.delta
  let deltaPercent = lastWave.deltaPercent
  let start = lastWave.start
  let end = lastWave.end
  let waveType = lastWave.type
  let direction = lastWave.direction

  // prediction
  let candlesLow = Infinity
  let candlesHigh = -Infinity

  for (let i = candles.length - 1; i >= 0; i--) {
    const candle = candles[i]
    if (candle.time > lastWave.end) {
      if (candle.high > high) {
        high = candle.high
        highUpdated = true
        delta = high - low

        if (lastWave.direction === 'up') {
          waveUpdated = true
          deltaPercent = delta / high
        } else {
          isNewWave = true
          start = candle.time //end
          direction = 'up'
          deltaPercent = delta / low
        }

        if (prevWave && candle.high > prevWave.high) {
          waveType = 'move'
        } else if (prevWave && candle.high <= prevWave.high) {
          waveType = 'pullback'
        }
      }

      if (candle.low < low) {
        low = candle.low
        lowUpdated = true
        delta = high - low

        if (lastWave.direction === 'down') {
          waveUpdated = true
          deltaPercent = delta / low
        } else {
          isNewWave = true
          start = candle.time // end
          direction = 'down'
          deltaPercent = delta / high
        }

        if (prevWave && candle.low < prevWave.low) {
          waveType = 'move'
        } else if (prevWave && candle.low >= prevWave.low) {
          waveType = 'pullback'
        }
      }

      delta = high - low
      end = candle.time

      hasNewCandles = true
      newCandles.unshift(candle)

      // prediction
      if (candle.high > candlesHigh) {
        candlesHigh = candle.high
      }
      if (candle.low < candlesLow) {
        candlesLow = candle.low
      }

    } else {
      break
    }
  }

  return {
    hasNewCandles,
    isNewWave,
    waveUpdated,
    highUpdated,
    lowUpdated,
    newCandles,
    type: waveType,
    direction,
    high,
    low,
    delta,
    deltaPercent,
    start,
    end,
  }
}

// ***********************************************
// Wave Ratio Stats
// ***********************************************

export function getWaveClusterStats(waves, field = 'deltaPercent', mode = 'direct') { // check results with delta, deltaPercent
  const tempInit = () => {
    return {
      any: [],
      up: [],
      down: [],
      // impulses
      move: [],
      moveUp: [],
      moveDown: [],
      // pullbacks
      pullback: [],
      pullbackUp: [],
      pullbackDown: [],
      // joined groups
      up_move: [],
      up_pullback: [],
      down_move: [],
      down_pullback: [],
    }
  }

  const temp = {
    move: tempInit(),
    pullback: tempInit(),
  }

  for (let i = 0; i < waves.length - 1; i++) {
    const wave = waves[i]
    const nextWave = waves[i + 1]
    const nwType = nextWave?.type || 'pullback'

    const value = mode === 'ratio'
      ? nextWave[field] / wave[field]
      : nextWave[field]

    temp[nwType].any.push(value)

    if (wave.direction === 'up') {
      temp[nwType].up.push(value)
      temp[nwType].up_move.push(value)
      temp[nwType].up_pullback.push(value)

      if (wave.type === 'move') {
        temp[nwType].move.push(value)
        temp[nwType].moveUp.push(value)
        temp[nwType].down_move.push(value)
      } else if (wave.type === 'pullback') {
        temp[nwType].pullback.push(value)
        temp[nwType].pullbackUp.push(value)
        temp[nwType].down_pullback.push(value)
      }
    } else if (wave.direction === 'down') {
      temp[nwType].down.push(value)
      temp[nwType].down_move.push(value)
      temp[nwType].down_pullback.push(value)

      if (wave.type === 'move') {
        temp[nwType].move.push(value)
        temp[nwType].moveDown.push(value)
        temp[nwType].up_move.push(value)
      } else if (wave.type === 'pullback') {
        temp[nwType].pullback.push(value)
        temp[nwType].pullbackDown.push(value)
        temp[nwType].up_pullback.push(value)
      }
    }

  }

  // console.log('temp getWaveClusterStats', temp)

  const results = { move: {}, pullback: {} }
  for (const category in temp) {

    for (const key in temp[category]) {
      const values = temp[category][key]
      const basicStats = calculateStatistics(values, true)

      const optimalClusters = getOptimalClustersDbscan(values, {
        initEpsilon: mode === 'ratio' ? 0.02 : 0.0004,
        minPointsPercent: 0.04,
        minPoints: 3,
        maxIterations: 10,
        maxNoisePercent: 0.20,
        iterationEpsilonMult: 1.5,
      })

      const { clusters, noise, iterations } = optimalClusters
      results[category][key] = {
        count: values.length,
        frequency: values.length / (waves.length - 1),
        clusters,
        stats: basicStats,
        noise,
        // clustersIterations: iterations
        // values,
      }
      // console.log('results', category, 'after', key, results[category][key])
    }
  }

  return results
}


export function getSpecificClusterStats(waves, filters, field = 'deltaPercent', mode = 'direct') { // check results with delta, deltaPercent
  const values = []

  for (let i = 0; i < waves.length - 1; i++) {
    const prevWave = waves[i]
    const lastWave = waves[i + 1]

    const paths = [
      `${lastWave.type}.${prevWave.type}${prevWave.direction.charAt(0).toUpperCase() + prevWave.direction.slice(1)}`,
      `${lastWave.type}.${prevWave.type}`,
      `${lastWave.type}.${prevWave.direction}`,
      `${lastWave.type}.any`,
    ]

    let allowed = false
    for (const filter of filters) {
      if (paths.includes(filter)) {
        allowed = true
        break
      }
    }
    if (allowed) {
      const value = mode === 'ratio'
        ? lastWave[field] / prevWave[field]
        : lastWave[field]
      values.push(value)
      // values.push(lastWave)
    }
  }

  const basicStats = calculateStatistics(values, true)//, simpleStatsEmaLength, field)

  const optimalClusters = getOptimalClustersDbscan(values, {
    initEpsilon: mode === 'ratio' ? 0.02 : 0.0004,
    minPointsPercent: 0.04,
    minPoints: 2,
    maxIterations: 10,
    maxNoisePercent: 0.20,
    iterationEpsilonMult: 1.5,
    // statisticOpts: [true, simpleStatsEmaLength, field],
    // distanceFunction: (a, b) => Math.abs(a[field] - b[field])
  })

  const { clusters, noise, iterations } = optimalClusters
  const results = {
    count: values.length,
    frequency: values.length / (waves.length - 1),
    clusters,
    stats: basicStats,
    noise,
    // clustersIterations: iterations
    // values,
  }

  return results
}


export function getClustersOutcome(waves, filters, clusters, field = 'deltaPercent', mode = 'direct') {

  function createStatsDataContainer() {
    return {
      delta: [], deltaPercent: [], duration: [], totalVolume: [], effortRatio: [],
      deltaRatioPrev: [], maxDiffPercent: [], maxDiffUpPercent: [], maxDiffDnPercent: [],
    };
  }

  function getZeroDist() {
    return {
      stdDev: [0, 0, 0, 0],
      mad: [0, 0, 0, 0],
      stdDevNeg: [0, 0, 0, 0],
      madNeg: [0, 0, 0, 0],
      stdDevPos: [0, 0, 0, 0],
      madPos: [0, 0, 0, 0],
    };
  }

  const buildStatsForCategory = (category) => {
    // Если данных нет, возвращаем пустую структуру
    if (!category || !category.duration || category.duration.length === 0) {
      return {
        count: 0,
        deltaPercent: { count: 0, distribution: getZeroDist() },
        deltaRatioPrev: { count: 0, distribution: getZeroDist() },
        effortRatio: { count: 0, distribution: getZeroDist() },
        totalVolume: { count: 0 },
        duration: { count: 0 },
        delta: { count: 0 },
        maxDiffPercent: { count: 0 },
        maxDiffUpPercent: { count: 0 },
        maxDiffDnPercent: { count: 0 },
      };
    }

    const stats = {
      count: category.duration.length,
      delta: calculateStatistics(category.delta),
      deltaPercent: calculateStatistics(category.deltaPercent, true),
      duration: calculateStatistics(category.duration),
      totalVolume: calculateStatistics(category.totalVolume),
      effortRatio: calculateStatistics(category.effortRatio),
      deltaRatioPrev: calculateStatistics(category.deltaRatioPrev),
      maxDiffPercent: calculateStatistics(category.maxDiffPercent),
      maxDiffUpPercent: calculateStatistics(category.maxDiffUpPercent),
      maxDiffDnPercent: calculateStatistics(category.maxDiffDnPercent),
    };

    // Добавляем распределения для ключевых метрик
    stats.deltaPercent.distribution = analyzeDistribution(category.deltaPercent, stats.deltaPercent);
    stats.deltaRatioPrev.distribution = analyzeDistribution(category.deltaRatioPrev, stats.deltaRatioPrev);
    stats.effortRatio.distribution = analyzeDistribution(category.effortRatio, stats.effortRatio);

    return stats;
  };

  const temp = []
  for (let i = 0; i < clusters.length; i++) {
    temp[i] = {
      move: createStatsDataContainer(),
      pullback: createStatsDataContainer(),
    };
  }


  // --- Шаг 2: Основной цикл по волнам ---
  for (let i = 1; i < waves.length - 1; i++) {
    const previousWave = waves[i - 1];
    const currentWave = waves[i];
    const nextWave = waves[i + 1];

    if (!previousWave || currentWave[field] === undefined || previousWave[field] === 0) continue;

    const value = mode === 'ratio'
      ? currentWave[field] / previousWave[field]
      : currentWave[field]

    const paths = [
      `${currentWave.type}.${previousWave.type}${previousWave.direction.charAt(0).toUpperCase() + previousWave.direction.slice(1)}`,
      `${currentWave.type}.${previousWave.type}`,
      `${currentWave.type}.${previousWave.direction}`,
      `${currentWave.type}.any`,
    ]

    let allowed = false
    for (const filter of filters) {
      if (paths.includes(filter)) {
        allowed = true
        break
      }
    }

    if (!allowed) continue

    let clusterIndex = -1;
    for (let j = 0; j < clusters.length; j++) {
      const cluster = clusters[j];
      if (value >= cluster.min && value <= cluster.max) {
        clusterIndex = j;
        break;
      }
    }

    if (clusterIndex === -1) continue; // Игнорируем "шум"

    const nextWaveType = nextWave.type || 'pullback';
    const targetContainer = temp[clusterIndex]?.[nextWaveType];

    if (targetContainer) {
      // --- Шаг 4: Сбор данных о nextWave ---
      const { delta, deltaPercent, maxDiffPercent, maxDiffUpPercent, maxDiffDnPercent, totalVolume, end, start } = nextWave;
      targetContainer.delta.push(delta);
      targetContainer.deltaPercent.push(deltaPercent);
      targetContainer.duration.push((end - start) / 60000);
      targetContainer.totalVolume.push(totalVolume);
      targetContainer.effortRatio.push(delta !== 0 ? totalVolume / Math.abs(deltaPercent) : 0);
      targetContainer.deltaRatioPrev.push(deltaPercent / currentWave.deltaPercent);
      targetContainer.maxDiffPercent.push(maxDiffPercent);
      targetContainer.maxDiffUpPercent.push(maxDiffUpPercent);
      targetContainer.maxDiffDnPercent.push(maxDiffDnPercent);
    }


    // let clusterIndex = -1;

    // for (let j = 0; j < clusters.length; j++) {
    //   const cluster = clusters[j];
    //   if (value >= cluster.min && value <= cluster.max) {
    //     clusterIndex = j;
    //     break;
    //   }
    // }

    // if (clusterIndex === -1) continue; // Игнорируем "шум"

    // const nextWaveType = nextWave.type || 'pullback';
    // const targetContainer = temp[clusterIndex]?.[nextWaveType];


    // if (targetContainer) {
    //   // --- Шаг 4: Сбор данных о nextWave ---
    //   const { delta, deltaPercent, maxDiffPercent, maxDiffUpPercent, maxDiffDnPercent, totalVolume, end, start } = nextWave;
    //   targetContainer.delta.push(delta);
    //   targetContainer.deltaPercent.push(deltaPercent);
    //   targetContainer.duration.push((end - start) / 60000);
    //   targetContainer.totalVolume.push(totalVolume);
    //   targetContainer.effortRatio.push(delta !== 0 ? totalVolume / Math.abs(deltaPercent) : 0);
    //   targetContainer.deltaRatioPrev.push(delta / currentWave.deltaPercent);
    //   targetContainer.maxDiffPercent.push(maxDiffPercent);
    //   targetContainer.maxDiffUpPercent.push(maxDiffUpPercent);
    //   targetContainer.maxDiffDnPercent.push(maxDiffDnPercent);
    // }

  }

  // --- Шаг 5: Финальный расчет статистики ---
  const finalResults = [];

  for (let i = 0; i < clusters.length; i++) {
    const clusterOutcomes = temp[i];

    if (clusterOutcomes) {
      const moveStats = buildStatsForCategory(clusterOutcomes.move);
      const pullbackStats = buildStatsForCategory(clusterOutcomes.pullback);

      finalResults.push({
        stats: { ...clusters[i] },
        outcomes: { move: moveStats, pullback: pullbackStats }
      });
    }
  }

  return finalResults;

}


export function analyzeOutcomesByCluster(waves, waveRatioStats, field = 'deltaPercent', mode = 'direct') {
  // Предполагается, что эти функции у вас есть и работают корректно
  // const buildStatsForCategory = (category) => { ... };
  // const createStatsDataContainer = () => { ... };

  function createStatsDataContainer() {
    return {
      delta: [], deltaPercent: [], duration: [], totalVolume: [], effortRatio: [],
      deltaRatioPrev: [], maxDiffPercent: [], maxDiffUpPercent: [], maxDiffDnPercent: [],
    };
  }

  // Предполагается, что у вас есть готовые функции calculateStatistics и analyzeDistribution
  // function calculateStatistics(values, useMad = false) { ... }
  // function analyzeDistribution(values, stats) { ... }

  function getZeroDist() {
    return {
      stdDev: [0, 0, 0, 0],
      mad: [0, 0, 0, 0],
      stdDevNeg: [0, 0, 0, 0],
      madNeg: [0, 0, 0, 0],
      stdDevPos: [0, 0, 0, 0],
      madPos: [0, 0, 0, 0],
    };
  }

  const buildStatsForCategory = (category) => {
    // Если данных нет, возвращаем пустую структуру
    if (!category || !category.duration || category.duration.length === 0) {
      return {
        count: 0,
        deltaPercent: { count: 0, distribution: getZeroDist() },
        deltaRatioPrev: { count: 0, distribution: getZeroDist() },
        effortRatio: { count: 0, distribution: getZeroDist() },
        totalVolume: { count: 0 },
        duration: { count: 0 },
        delta: { count: 0 },
        maxDiffPercent: { count: 0 },
        maxDiffUpPercent: { count: 0 },
        axDiffDnPercent: { count: 0 },
      };
    }

    const stats = {
      count: category.duration.length,
      delta: calculateStatistics(category.delta),
      deltaPercent: calculateStatistics(category.deltaPercent, true),
      duration: calculateStatistics(category.duration),
      totalVolume: calculateStatistics(category.totalVolume),
      effortRatio: calculateStatistics(category.effortRatio),
      deltaRatioPrev: calculateStatistics(category.deltaRatioPrev),
      maxDiffPercent: calculateStatistics(category.maxDiffPercent),
      maxDiffUpPercent: calculateStatistics(category.maxDiffUpPercent),
      maxDiffDnPercent: calculateStatistics(category.maxDiffDnPercent),
    };

    // Добавляем распределения для ключевых метрик
    stats.deltaPercent.distribution = analyzeDistribution(category.deltaPercent, stats.deltaPercent);
    stats.deltaRatioPrev.distribution = analyzeDistribution(category.deltaRatioPrev, stats.deltaRatioPrev);
    stats.effortRatio.distribution = analyzeDistribution(category.effortRatio, stats.effortRatio);

    return stats;
  };

  // --- Шаг 1: Инициализация сложной структуры для сбора сырых данных ---
  const tempOutcomes = {};
  for (const waveType of ['move', 'pullback']) {
    tempOutcomes[waveType] = {};
    for (const subCategoryKey in waveRatioStats[waveType]) {
      tempOutcomes[waveType][subCategoryKey] = {};
      const clusters = waveRatioStats[waveType]?.[subCategoryKey]?.clusters || [];
      for (let i = 0; i < clusters.length; i++) {
        tempOutcomes[waveType][subCategoryKey][`cluster_${i}`] = {
          move: createStatsDataContainer(),
          pullback: createStatsDataContainer(),
        };
      }
    }
  }

  // --- Шаг 2: Основной цикл по волнам ---
  for (let i = 1; i < waves.length - 1; i++) {
    const previousWave = waves[i - 1];
    const currentWave = waves[i];

    const nextWave = waves[i + 1];

    if (!previousWave || currentWave[field] === undefined || previousWave[field] === 0) continue;

    // const value = currentWave.deltaPercent / previousWave.deltaPercent;
    const value = mode === 'ratio'
      ? currentWave[field] / previousWave[field]
      : currentWave[field]

    const prevWaveDir = previousWave.direction;
    const prevWaveType = previousWave.type || 'pullback';
    const currentWaveType = currentWave.type || 'pullback';
    const nextWaveType = nextWave.type || 'pullback';

    // --- Шаг 3: Определяем все подкатегории для текущей пары волн ---
    // const applicableSubCategories = [
    //   `${prevWaveType}${prevWaveDir.charAt(0).toUpperCase() + prevWaveDir.slice(1)}`, // 'moveUp', 'pullbackDown', etc.
    //   prevWaveType, // 'move' или 'pullback'
    //   prevWaveDir, // 'up' или 'down'
    //   // `${prevWaveDir}_${prevWaveType}`, // direction or type match 
    //   `${prevWaveDir}_pullback`,
    //   `${prevWaveDir}_move`, 
    //   `up_${prevWaveType}`, 
    //   `down_${prevWaveType}`, 
    //   'any'
    // ];
    // Используем Set, чтобы избежать дубликатов ключей
    const applicableSubCategories = new Set([
      `${prevWaveType}${prevWaveDir.charAt(0).toUpperCase() + prevWaveDir.slice(1)}`,
      prevWaveType, // 'move' или 'pullback'
      prevWaveDir,  // 'up' или 'down'
      'any'
    ]);

    if (prevWaveDir === 'up' || prevWaveType === 'move') {
      applicableSubCategories.add('up_move');
    }
    if (prevWaveDir === 'down' || prevWaveType === 'move') {
      applicableSubCategories.add('down_move');
    }
    if (prevWaveDir === 'up' || prevWaveType === 'pullback') {
      applicableSubCategories.add('up_pullback');
    }
    if (prevWaveDir === 'down' || prevWaveType === 'pullback') {
      applicableSubCategories.add('down_pullback');
    }

    // Проверяем каждую из 4-х возможных подкатегорий
    for (const subCatKey of applicableSubCategories) {
      const clustersData = waveRatioStats[currentWaveType]?.[subCatKey]?.clusters;
      if (!clustersData) { continue; }

      let clusterIndex = -1;
      for (let j = 0; j < clustersData.length; j++) {
        const cluster = clustersData[j];
        if (value >= cluster.min && value <= cluster.max) {
          clusterIndex = j;
          break;
        }
      }

      if (clusterIndex === -1) {
        continue; // Игнорируем "шум"
      }

      const clusterId = `cluster_${clusterIndex}`;
      const targetContainer = tempOutcomes[currentWaveType]?.[subCatKey]?.[clusterId]?.[nextWaveType];

      if (targetContainer) {
        // --- Шаг 4: Сбор данных о nextWave ---
        const { delta, deltaPercent, maxDiffPercent, maxDiffUpPercent, maxDiffDnPercent, totalVolume, end, start } = nextWave;
        targetContainer.delta.push(delta);
        targetContainer.deltaPercent.push(deltaPercent);
        targetContainer.duration.push((end - start) / 60000);
        targetContainer.totalVolume.push(totalVolume);
        targetContainer.effortRatio.push(delta !== 0 ? totalVolume / Math.abs(deltaPercent) : 0);
        targetContainer.deltaRatioPrev.push(deltaPercent / currentWave.deltaPercent);
        targetContainer.maxDiffPercent.push(maxDiffPercent);
        targetContainer.maxDiffUpPercent.push(maxDiffUpPercent);
        targetContainer.maxDiffDnPercent.push(maxDiffDnPercent);
      }
    }
  }

  // console.log('tempOutcomes', tempOutcomes)

  // --- Шаг 5: Финальный расчет статистики ---
  const finalResults = { move: {}, pullback: {} };
  for (const waveType in waveRatioStats) {
    finalResults[waveType] = {};
    for (const subCatKey in waveRatioStats[waveType]) {
      finalResults[waveType][subCatKey] = {
        ...waveRatioStats[waveType][subCatKey], // Копируем исходные данные (count, frequency, etc.)
        clusters: [] // Перезаписываем кластеры нашими новыми данными
      };

      const clustersData = waveRatioStats[waveType]?.[subCatKey]?.clusters || [];
      for (let i = 0; i < clustersData.length; i++) {
        const clusterId = `cluster_${i}`;
        const clusterOutcomes = tempOutcomes[waveType]?.[subCatKey]?.[clusterId];

        if (clusterOutcomes) {
          const moveStats = buildStatsForCategory(clusterOutcomes.move);
          const pullbackStats = buildStatsForCategory(clusterOutcomes.pullback);

          finalResults[waveType][subCatKey].clusters.push({
            stats: { ...clustersData[i] },
            outcomes: { move: moveStats, pullback: pullbackStats }
          });

        }
      }
    }
  }

  return finalResults;
}


export function getOptimalClustersDbscan(values, opts = {
  initEpsilon: 0.02,
  minPointsPercent: 0.04,
  minPoints: 3,
  maxIterations: 5,
  maxNoisePercent: 0.15,
  iterationEpsilonMult: 1.5,
  statisticOpts: [],
  distanceFunction: undefined
}) {
  let epsilon = opts.initEpsilon || 0.02
  const minPointsPercent = opts.minPointsPercent || 0.04
  const minPoints = opts.minPoints || 3
  const maxIterations = opts.maxIterations || 5
  const maxNoisePercent = opts.maxNoisePercent || 0.15
  const iterationEpsilonMult = opts.iterationEpsilonMult || 1.5

  let clusters
  let iterations = []

  for (let i = 0; i < maxIterations; i++) {
    const clustersIter = clusterizeDbscan(values, epsilon, minPointsPercent, minPoints, opts.distanceFunction, opts.statisticOpts)
    iterations.push({
      epsilon,
      clusters: clustersIter
    })

    if (clustersIter.noise.length / values.length < maxNoisePercent) {
      clusters = clustersIter
      break
    } else {
      epsilon *= iterationEpsilonMult
    }
  }

  if (!clusters) {
    clusters = iterations[iterations.length - 1].clusters
  }

  const { stats, noise, dbscan, clusters: dbclusters } = clusters // , dbscan, clusters

  return {
    clusters: stats,
    noise,
    iterations,
    // dbscan,
    // dbclusters
  }
}

// sorted version
export function clusterizeDbscan(values, epsilon = 0.05, minPointsPercent = 0.05, minPoints = 3, distanceFunction, statisticOpts = []) {
  const clustering = dbscan({
    dataset: values,
    epsilon,
    minimumPoints: Math.max(values.length * minPointsPercent, minPoints),
    distanceFunction
    // distanceFunction: (a, b) => Math.abs(a.created - b.created)
  });
  // console.log("Clusters dbscan:", clustering);

  // map indexes to values
  const noise = clustering.noise.map(index => values[index]);
  const mappedClusters = clustering.clusters.map(cluster =>
    cluster.map(index => values[index])
  );
  // console.log("Clusters dbscan mapped:", mappedClusters, 'noise', noise);

  const clustersStats = mappedClusters.map(cluster => calculateStatistics(cluster, ...statisticOpts));

  // Создаем массив объектов для сортировки
  const combined = clustersStats.map((stat, i) => {
    stat.distribution = analyzeDistribution(mappedClusters[i], stat);
    const res = {
      stat,
      cluster: clustering.clusters[i],
      mappedCluster: mappedClusters[i]
    }
    return res
  });

  // Сортируем по возрастанию avg
  combined.sort((a, b) => a.stat.avg - b.stat.avg);

  // Извлекаем отсортированные массивы
  // const sortedClusters = combined.map(item => item.cluster);
  // const sortedMappedClusters = combined.map(item => item.mappedCluster);
  const sortedStats = combined.map(item => item.stat);

  return {
    stats: sortedStats,
    noise,
    // dbscan: { ...clustering, clusters: sortedClusters },
    // clusters: sortedMappedClusters,
  };
}


export function clusterizeKmeans(values) {
  // Функция для нормализации данных
  function normalize(data) {
    let mean = data.reduce((sum, value) => sum + value, 0) / data.length;
    let std = Math.sqrt(data.reduce((sum, value) => sum + Math.pow(value - mean, 2), 0) / data.length);
    return data.map(value => [(value - mean) / std]);
  }

  // Нормализация данных
  let normalized = values.map(value => [value]); //normalize(values);
  console.log('clusterize initial:', values, "normalized:", normalized);

  function getInertias(data) {
    let inertias = [];
    const kMax = Math.min(10, data.length);
    for (let k = 1; k <= kMax; k++) {
      let result = kmeans(data, k);
      let inertia = 0;
      for (let i = 0; i < data.length; i++) {
        const point = data[i];
        const centroid = result.centroids[result.clusters[i]];
        const distance = Math.pow(point[0] - centroid[0], 2);
        inertia += distance;
      }
      inertias.push(inertia);
    }
    return inertias
  }


  // Вывод значений инерции для разных количеств кластеров
  const inertias = getInertias(normalized);
  console.log("Inertias:", inertias);

  // Кластеризация
  let clusters = kmeans(normalized, 3);

  console.log("Clusters:", clusters);

  function remapClusters(clusters) {
    // Создаем карту сортировки центроидов
    const sortedCentroidIndexes = clusters.centroids
      .map((value, index) => ({ index, value: value[0] })) // Используем value[0], так как центроиды у вас вложенные массивы
      .sort((a, b) => a.value - b.value)
      .map((centroid, newIndex) => ({ oldIndex: centroid.index, newIndex }));

    console.log("sortedCentroidIndexes:", sortedCentroidIndexes);
    // Создаем объект для ремапа
    const remap = sortedCentroidIndexes.reduce((acc, curr) => {
      acc[curr.oldIndex] = curr.newIndex;
      return acc;
    }, {});

    console.log("Map for remapping clusters:", remap, clusters);
    // Применение ремапа к вашим кластерам
    const remappedClusters = clusters.clusters.map(cluster => remap[cluster]);

    // console.log("Remapped clusters:", remappedClusters);
    return remappedClusters
  }

  const remappedClusters = remapClusters(clusters);

  // Вывод результатов
  console.log("Clusters remapped:", remappedClusters);

  return remappedClusters
}

// ***********************************************
// Breakouts & Bounces
// ***********************************************

// Предполагается, что эти функции импортированы и доступны
// import { calculateStatistics } from './stats';
// import { clusterizeDbscan } from './external-module';

// Ваши оригинальные, рабочие функции, которые мы используем как "черный ящик"
// Предполагается, что они доступны в этом скоупе.
// function getWaveExtremStats_original(waves) { ... }
// function calculateStatistics(...) { ... }
// function clusterizeDbscan(...) { ... }

export class ExtremumStatsManager {
  constructor() {
    // Состояние - это кэш последних входных данных и результата
    this.lastWaves = null;
    this.cachedResult = null;
  }

  /**
   * Получает статистику. Если входные данные (волны) не изменились,
   * возвращает кэшированный результат. Иначе - запускает полный перерасчет.
   * @param {Array<Object>} waves - ПОЛНЫЙ и актуальный массив волн.
   * @returns {Object} - Результат вычисления статистики.
   */
  get(waves) {
    // Простая проверка, изменились ли данные. Сравниваем длину и последнюю волну.
    if (
      this.lastWaves &&
      this.cachedResult &&
      this.lastWaves.length === waves.length &&
      this.lastWaves[this.lastWaves.length - 1]?.startTime === waves[waves.length - 1]?.startTime &&
      this.lastWaves[this.lastWaves.length - 1]?.endTime === waves[waves.length - 1]?.endTime
    ) {
      // Данные не изменились, возвращаем кэш
      return this.cachedResult;
    }

    // Данные изменились. Запускаем полный перерасчет, используя вашу оригинальную функцию.
    // Это гарантирует 100% точность.
    const result = getWaveExtremStats(waves); // getWaveExtremStats - это ваша исходная функция

    // Кэшируем новые данные и результат для будущих вызовов
    this.lastWaves = waves;
    this.cachedResult = result;

    return result;
  }
}

// ПРИМЕЧАНИЕ:
// Вам нужно будет переименовать вашу оригинальную функцию, например, в
// getWaveExtremStats_original, чтобы избежать конфликта имен, и убедиться,
// что она доступна для вызова из метода get().
export class ExtremumStatsManager2 {
  constructor() {
    // Состояние для инкрементального поиска событий
    this.allWaves = [];
    this.highStack = [];
    this.lowStack = [];
    this.processedWavesCount = 0;

    // Объект для хранения сырых результатов (событий)
    this.results = {
      cascades: { any: [], up: [], down: [] },
      single: {
        up: { trueBreakouts: [], falseBreakouts: [], falseBreakouts1: [], falseBreakouts2: [] },
        down: { trueBreakouts: [], falseBreakouts: [], falseBreakouts1: [], falseBreakouts2: [] },
      },
      pullbacks: { any: [], up: [], down: [] }
    };

    // Состояние для кэширования тяжелых вычислений
    this.cachedStats = null;
    this.statsAreDirty = true;
  }

  /**
   * Легкий и быстрый метод для добавления новых волн и инкрементального поиска событий.
   * @param {Array<Object>} newWaves - Массив только НОВЫХ волн.
   */
  addWaves(newWaves) {
    if (newWaves.length === 0) return;

    this.allWaves.push(...newWaves);
    this.statsAreDirty = true; // Помечаем, что статистика устарела

    for (let i = this.processedWavesCount; i < this.allWaves.length; i++) {
      const wave = this.allWaves[i];

      if (wave.direction === 'up') {
        const breakCandidates = [];
        let newHigh = null;
        for (let j = this.highStack.length - 1; j >= 0; j--) {
          const closestHighIdx = this.highStack[j];
          const closestHigh = this.allWaves[closestHighIdx];
          if (wave.high > closestHigh.high) {
            const info = { prevIdx: closestHighIdx, newIdx: i, prevHigh: closestHigh.high, newHigh: wave.high };
            if (i + 1 < this.allWaves.length) {
              const nextWave = this.allWaves[i + 1];
              info.falseBreakout1 = nextWave.low < closestHigh.high;
            } else { info.falseBreakout1 = null; }
            if (i + 3 < this.allWaves.length) {
              const nextWave = this.allWaves[i + 3];
              info.falseBreakout2 = nextWave.low < closestHigh.high;
            } else { info.falseBreakout2 = null; }
            breakCandidates.push(info);
            if (info.falseBreakout1 || info.falseBreakout2) { this.results.single.up.falseBreakouts.push(info); }
            if (info.falseBreakout1) { this.results.single.up.falseBreakouts1.push(info); }
            if (!info.falseBreakout1 && info.falseBreakout2) { this.results.single.up.falseBreakouts2.push(info); }
            if (!info.falseBreakout1 && !info.falseBreakout2) { this.results.single.up.trueBreakouts.push(info); }
            this.highStack.pop();
            newHigh = i;
          }
        }
        if (this.highStack.length) {
          const info = { prevIdx: this.highStack[this.highStack.length - 1], newIdx: i, prevHigh: this.allWaves[this.highStack[this.highStack.length - 1]].high, newHigh: wave.high };
          this.results.pullbacks.down.push(info); this.results.pullbacks.any.push(info);
        }
        this.highStack.push(i);
        if (newHigh !== null) {
          const info = { direction: 'up', waveId: newHigh, beaten: breakCandidates.map(b => b.prevIdx), extra: breakCandidates };
          this.results.cascades.up.push(info); this.results.cascades.any.push(info);
        }
      } else if (wave.direction === 'down') {
        const breakCandidates = [];
        let newLow = null;
        for (let j = this.lowStack.length - 1; j >= 0; j--) {
          const closestLowIdx = this.lowStack[j];
          const closestLow = this.allWaves[closestLowIdx];
          if (wave.low < closestLow.low) {
            const info = { prevIdx: closestLowIdx, newIdx: i, prevLow: closestLow.low, newLow: wave.low };
            if (i + 1 < this.allWaves.length) {
              const nextWave = this.allWaves[i + 1];
              info.falseBreakout1 = nextWave.high > closestLow.low;
            } else { info.falseBreakout1 = null; }
            if (i + 3 < this.allWaves.length) {
              const nextWave = this.allWaves[i + 3];
              info.falseBreakout2 = nextWave.high > closestLow.low;
            } else { info.falseBreakout2 = null; }
            breakCandidates.push(info);
            if (info.falseBreakout1 || info.falseBreakout2) { this.results.single.down.falseBreakouts.push(info); }
            if (info.falseBreakout1) { this.results.single.down.falseBreakouts1.push(info); }
            if (!info.falseBreakout1 && info.falseBreakout2) { this.results.single.down.falseBreakouts2.push(info); }
            if (!info.falseBreakout1 && !info.falseBreakout2) { this.results.single.down.trueBreakouts.push(info); }
            this.lowStack.pop();
            newLow = i;
          }
        }
        if (this.lowStack.length) {
          const info = { prevIdx: this.lowStack[this.lowStack.length - 1], newIdx: i, prevLow: this.allWaves[this.lowStack[this.lowStack.length - 1]].low, newLow: wave.low };
          this.results.pullbacks.up.push(info); this.results.pullbacks.any.push(info);
        }
        this.lowStack.push(i);
        if (newLow !== null) {
          const info = { direction: 'down', waveId: newLow, beaten: breakCandidates.map(b => b.prevIdx), extra: breakCandidates };
          this.results.cascades.down.push(info); this.results.cascades.any.push(info);
        }
      }
    }
    this.processedWavesCount = this.allWaves.length;
  }

  /**
   * "Тяжелый" метод. Выполняет полный расчет статистики и кластеризации,
   * но кэширует результат, чтобы не делать это повторно без необходимости.
   */
  getStats() {
    if (!this.statsAreDirty && this.cachedStats !== null) {
      return this.cachedStats;
    }

    // ===================================================================================
    // НАЧАЛО ВАШЕГО ОРИГИНАЛЬНОГО КОДА ДЛЯ РАСЧЕТА СТАТИСТИКИ
    // ===================================================================================

    const singleStats = { up: {}, down: {} };
    const singleTemp = { up: {}, down: {} };
    const singleFields = [
      { field: 'deltaPercent', epsilon: 0.0005, minPointsPercent: 0.03 },
      { field: 'prevRatio', epsilon: 0.01, minPointsPercent: 0.03 },
      { field: 'lastRatio', epsilon: 0.01, minPointsPercent: 0.01 }
    ];
    const singleMaxExtendEpsilonSteps = 5;
    const singleMaxNoisePercent = 0.15;

    // --- Breakout stats ---
    for (const key in this.results.single.up) {
      singleTemp.up[key] = [];
      for (const result of this.results.single.up[key]) {
        const w1 = this.allWaves[result.prevIdx];
        const w2 = this.allWaves[result.newIdx];
        const wLast = this.allWaves[result.newIdx - 1];
        const delta = w2.high - w1.high;
        const deltaPercent = delta / w1.high;
        const prevRatio = delta / w1.delta;
        const currentRatio = delta / w2.delta;
        const lastRatio = delta / wLast.delta;
        singleTemp.up[key].push({ delta, deltaPercent, prevRatio, currentRatio, lastRatio });
      }
      singleStats.up[key] = {};
      for (const variant of singleFields) {
        const values = singleTemp.up[key].map(item => item[variant.field]);
        const basicStats = calculateStatistics(values, true);
        let clusters;
        let clustersIterations = [];
        let epsilon = variant.epsilon;
        for (let i = 0; i < singleMaxExtendEpsilonSteps; i++) {
          const clustersIter = clusterizeDbscan(values, epsilon, variant.minPointsPercent);
          clustersIterations.push({ epsilon, clusters: clustersIter });
          if (clustersIter.noise.length / values.length < singleMaxNoisePercent) {
            clusters = clustersIter;
            break;
          } else {
            epsilon *= 2;
          }
        }
        if (!clusters) {
          clusters = clustersIterations[clustersIterations.length - 1].clusters;
        }
        singleStats.up[key][variant.field] = {
          values, stats: basicStats, clusters,
          coverage: 1 - clusters.noise.length / values.length,
          clustersIterations
        };
      }
    }

    for (const key in this.results.single.down) {
      singleTemp.down[key] = [];
      for (const result of this.results.single.down[key]) {
        const w1 = this.allWaves[result.prevIdx];
        const w2 = this.allWaves[result.newIdx];
        const wLast = this.allWaves[result.newIdx - 1];
        const delta = w1.low - w2.low;
        const deltaPercent = delta / w1.low;
        const prevRatio = delta / w1.delta;
        const currentRatio = delta / w2.delta;
        const lastRatio = delta / wLast.delta;
        singleTemp.down[key].push({ delta, deltaPercent, prevRatio, currentRatio, lastRatio });
      }
      singleStats.down[key] = {};
      for (const variant of singleFields) {
        const values = singleTemp.down[key].map(item => item[variant.field]);
        const basicStats = calculateStatistics(values, true);
        let clusters;
        let clustersIterations = [];
        let epsilon = variant.epsilon;
        for (let i = 0; i < singleMaxExtendEpsilonSteps; i++) {
          const clustersIter = clusterizeDbscan(values, epsilon, variant.minPointsPercent);
          clustersIterations.push({ epsilon, clusters: clustersIter });
          if (clustersIter.noise.length / values.length < singleMaxNoisePercent) {
            clusters = clustersIter;
            break;
          } else {
            epsilon *= 2;
          }
        }
        if (!clusters) {
          clusters = clustersIterations[clustersIterations.length - 1].clusters;
        }
        singleStats.down[key][variant.field] = {
          values, stats: basicStats, clusters,
          coverage: 1 - clusters.noise.length / values.length,
          clustersIterations
        };
      }
    }

    // --- Bounce stats ---
    const pullbackStats = { up: {}, down: {} };
    const pullbackTemp = { up: [], down: [] };
    const pullbackFields = [
      { field: 'deltaPercent', epsilon: 0.0001, minPointsPercent: 0.01 },
      { field: 'prevRatio', epsilon: 0.01, minPointsPercent: 0.01 },
      { field: 'lastRatio', epsilon: 0.01, minPointsPercent: 0.01 }
    ];
    const pullbackMaxExtendEpsilonSteps = 5;
    const pullbackMaxNoisePercent = 0.15;

    for (const result of this.results.pullbacks.down) {
      const w1 = this.allWaves[result.prevIdx];
      const w2 = this.allWaves[result.newIdx];
      const wLast = this.allWaves[result.newIdx - 1];
      const delta = w1.high - w2.high;
      const deltaPercent = delta / w1.high;
      const prevRatio = delta / w1.delta;
      const currentRatio = delta / w2.delta;
      const lastRatio = delta / wLast.delta;
      pullbackTemp.down.push({ delta, deltaPercent, prevRatio, currentRatio, lastRatio });
    }

    for (const result of this.results.pullbacks.up) {
      const w1 = this.allWaves[result.prevIdx];
      const w2 = this.allWaves[result.newIdx];
      const wLast = this.allWaves[result.newIdx - 1];
      const delta = w2.low - w1.low;
      const deltaPercent = delta / w1.low;
      const prevRatio = delta / w1.delta;
      const currentRatio = delta / w2.delta;
      const lastRatio = delta / wLast.delta;
      pullbackTemp.up.push({ delta, deltaPercent, prevRatio, currentRatio, lastRatio });
    }

    for (const variant of pullbackFields) {
      const values = pullbackTemp.down.map(item => item[variant.field]);
      const basicStats = calculateStatistics(values, true);
      let clusters;
      let clustersIterations = [];
      let epsilon = variant.epsilon;
      for (let i = 0; i < pullbackMaxExtendEpsilonSteps; i++) {
        const clustersIter = clusterizeDbscan(values, epsilon, variant.minPointsPercent);
        clustersIterations.push({ epsilon, clusters: clustersIter });
        if (clustersIter.noise.length / values.length < pullbackMaxNoisePercent) {
          clusters = clustersIter;
          break;
        } else {
          epsilon *= 2;
        }
      }
      if (!clusters) {
        clusters = clustersIterations[clustersIterations.length - 1].clusters;
      }
      pullbackStats.down[variant.field] = {
        values, stats: basicStats, clusters,
        coverage: 1 - clusters.noise.length / values.length,
        clustersIterations
      };
    }

    for (const variant of pullbackFields) {
      const values = pullbackTemp.up.map(item => item[variant.field]);
      const basicStats = calculateStatistics(values, true);
      let clusters;
      let clustersIterations = [];
      let epsilon = variant.epsilon;
      for (let i = 0; i < pullbackMaxExtendEpsilonSteps; i++) {
        const clustersIter = clusterizeDbscan(values, epsilon, variant.minPointsPercent);
        clustersIterations.push({ epsilon, clusters: clustersIter });
        if (clustersIter.noise.length / values.length < pullbackMaxNoisePercent) {
          clusters = clustersIter;
          break;
        } else {
          epsilon *= 2;
        }
      }
      if (!clusters) {
        clusters = clustersIterations[clustersIterations.length - 1].clusters;
      }
      pullbackStats.up[variant.field] = {
        values, stats: basicStats, clusters,
        coverage: 1 - clusters.noise.length / values.length,
        clustersIterations
      };
    }

    // ===================================================================================
    // КОНЕЦ ВАШЕГО ОРИГИНАЛЬНОГО КОДА
    // ===================================================================================

    const finalResult = {
      data: this.results,
      stats: {
        single: singleStats,
        pullback: pullbackStats,
      }
    };

    this.cachedStats = finalResult;
    this.statsAreDirty = false;

    return finalResult;
  }
}

export function getWaveExtremStats(waves) {
  const highStack = [];
  const lowStack = [];

  const results = {
    // BREAKOUTS
    cascades: {
      any: [],
      up: [],
      down: [],
    },
    // single level
    single: {
      up: {
        trueBreakouts: [],
        falseBreakouts: [],
        falseBreakouts1: [],
        falseBreakouts2: [],
      },
      down: {
        trueBreakouts: [],
        falseBreakouts: [],
        falseBreakouts1: [],
        falseBreakouts2: [],
      },
    },
    // PULLBACKS
    pullbacks: {
      any: [],
      up: [],
      down: [],
    }
  }

  for (let i = 0; i < waves.length; i++) {
    const wave = waves[i];
    // console.log('getWaveExtremStats', i, highStack, breakouts)

    if (wave.direction === 'up') {
      const breakCandidates = []
      let newHigh = null

      for (let j = highStack.length - 1; j >= 0; j--) {
        const closestHighIdx = highStack[j];
        const closestHigh = waves[closestHighIdx]

        if (wave.high > closestHigh.high) {
          const info = {
            prevIdx: closestHighIdx,
            newIdx: i,
            prevHigh: closestHigh.high,
            newHigh: wave.high
          }

          // false brakeout check 1
          if (i + 1 < waves.length - 1) {
            const nextWave = waves[i + 1]
            if (nextWave.low < closestHigh.high) {
              info.falseBreakout1 = true
            } else {
              info.falseBreakout1 = false
            }
          } else {
            info.falseBreakout1 = null
          }
          // false brakeout check 2
          if (i + 3 < waves.length - 1) {
            const nextWave = waves[i + 3]
            if (nextWave.low < closestHigh.high) {
              info.falseBreakout2 = true
            } else {
              info.falseBreakout2 = false
            }
          } else {
            info.falseBreakout2 = null
          }

          breakCandidates.push(info);

          if (info.falseBreakout1 || info.falseBreakout2) {
            results.single.up.falseBreakouts.push(info)
          }
          if (info.falseBreakout1) {
            results.single.up.falseBreakouts1.push(info)
          }
          if (!info.falseBreakout1 && info.falseBreakout2) {
            results.single.up.falseBreakouts2.push(info)
          }
          if (!info.falseBreakout1 && !info.falseBreakout2) {
            results.single.up.trueBreakouts.push(info)
          }

          highStack.pop();
          newHigh = i;
        }
      }

      if (highStack.length) {
        const prevHigh = waves[highStack[highStack.length - 1]].high

        const info = {
          prevIdx: highStack[highStack.length - 1],
          newIdx: i,
          prevHigh,
          newHigh: wave.high
        }

        results.pullbacks.down.push(info)
        results.pullbacks.any.push(info)
      }

      highStack.push(i);

      if (newHigh !== null) {
        const info = {
          direction: 'up',
          waveId: newHigh,
          beaten: breakCandidates.map(b => b.prevIdx),
          extra: breakCandidates
        }
        results.cascades.up.push(info)
        results.cascades.any.push(info)
        // console.log('getWaveExtremStats iteration', i, 'higher high', waves[i].high) //, 'highStack', highStack, breakouts)
      } else {
        // console.log('getWaveExtremStats iteration', i, 'lower high', waves[i].high) //, 'highStack', highStack, breakouts)
      }

    } else if (wave.direction === 'down') {
      const breakCandidates = []
      let newLow = null

      for (let j = lowStack.length - 1; j >= 0; j--) {
        const closestLowIdx = lowStack[j];
        const closestLow = waves[closestLowIdx]

        if (wave.low < closestLow.low) {
          const info = {
            prevIdx: closestLowIdx,
            newIdx: i,
            prevLow: closestLow.low,
            newLow: wave.low
          }

          // false brakeout check 1
          if (i + 1 < waves.length - 1) {
            const nextWave = waves[i + 1]
            if (nextWave.high > closestLow.low) {
              info.falseBreakout1 = true
            } else {
              info.falseBreakout1 = false
            }
          } else {
            info.falseBreakout1 = null
          }
          // false brakeout check 2
          if (i + 3 < waves.length - 1) {
            const nextWave = waves[i + 3]
            if (nextWave.high > closestLow.low) {
              info.falseBreakout2 = true
            } else {
              info.falseBreakout2 = false
            }
          } else {
            info.falseBreakout2 = null
          }

          breakCandidates.push(info);

          if (info.falseBreakout1 || info.falseBreakout2) {
            results.single.down.falseBreakouts.push(info)
          }
          if (info.falseBreakout1) {
            results.single.down.falseBreakouts1.push(info)
          }
          if (!info.falseBreakout1 && info.falseBreakout2) {
            results.single.down.falseBreakouts2.push(info)
          }
          if (!info.falseBreakout1 && !info.falseBreakout2) {
            results.single.down.trueBreakouts.push(info)
          }

          lowStack.pop();
          newLow = i;
        }
      }

      if (lowStack.length) {
        const info = {
          prevIdx: lowStack[lowStack.length - 1],
          newIdx: i,
          prevLow: waves[lowStack[lowStack.length - 1]].low,
          newLow: wave.low
        }
        results.pullbacks.up.push(info)
        results.pullbacks.any.push(info)
      }

      lowStack.push(i);

      if (newLow !== null) {
        const info = {
          direction: 'down',
          waveId: newLow,
          beaten: breakCandidates.map(b => b.prevIdx),
          extra: breakCandidates
        }
        results.cascades.down.push(info)
        results.cascades.any.push(info)
        // console.log('getWaveExtremStats iteration', i, 'lower low', waves[i].low) //, 'lowStack', lowStack, breakouts)
      } else {
        // console.log('getWaveExtremStats iteration', i, 'higher low', waves[i].low) //, 'lowStack', lowStack, breakouts)
      }
    }

  }

  // console.log("getWaveExtremStats results:", results)

  const singleStats = { up: {}, down: {} }
  const singleTemp = { up: {}, down: {} }

  const singleFields = [
    // {
    //   field: 'delta',
    //   epsilon: 0.05, // need to use coin min price step size calculations
    //   minPointsPercent: 0.05
    // },
    {
      field: 'deltaPercent',
      epsilon: 0.0005, // 0.05% ~ average market order fee
      minPointsPercent: 0.03
    },
    {
      field: 'prevRatio',
      epsilon: 0.01,
      minPointsPercent: 0.03
    },
    // {
    //   field: 'currentRatio',
    //   epsilon: 0.025,
    //   minPointsPercent: 0.03
    // },
    {
      field: 'lastRatio',
      epsilon: 0.01,
      minPointsPercent: 0.01
    }
  ]

  const singleMaxExtendEpsilonSteps = 5
  const singleMaxNoisePercent = 0.15

  // ****************************************************
  // brakeout stats
  // ****************************************************

  for (const key in results.single.up) {
    singleTemp.up[key] = []
    for (const result of results.single.up[key]) {
      const w1 = waves[result.prevIdx]
      const w2 = waves[result.newIdx]
      const wLast = waves[result.newIdx - 1]
      const delta = w2.high - w1.high
      const deltaPercent = delta / w1.high
      const prevRatio = delta / w1.delta
      const currentRatio = delta / w2.delta
      const lastRatio = delta / wLast.delta
      singleTemp.up[key].push({ delta, deltaPercent, prevRatio, currentRatio, lastRatio })
    }

    singleStats.up[key] = {}
    for (const variant of singleFields) {
      const values = singleTemp.up[key].map(item => item[variant.field])
      const basicStats = calculateStatistics(values, true)

      let clusters
      let clustersIterations = []
      let epsilon = variant.epsilon
      for (let i = 0; i < singleMaxExtendEpsilonSteps; i++) {
        const clustersIter = clusterizeDbscan(values, epsilon, variant.minPointsPercent)
        clustersIterations.push({
          epsilon,
          clusters: clustersIter
        })

        if (clustersIter.noise.length / values.length < singleMaxNoisePercent) {
          clusters = clustersIter
          break
        } else {
          epsilon *= 2
        }
      }

      if (!clusters) {
        clusters = clustersIterations[clustersIterations.length - 1].clusters
      }

      singleStats.up[key][variant.field] = {
        values,
        stats: basicStats,
        clusters,
        coverage: 1 - clusters.noise.length / values.length,
        clustersIterations
      }
    }
  }

  for (const key in results.single.down) {
    singleTemp.down[key] = []
    for (const result of results.single.down[key]) {
      const w1 = waves[result.prevIdx]
      const w2 = waves[result.newIdx]
      const wLast = waves[result.newIdx - 1]
      const delta = w1.low - w2.low
      const deltaPercent = delta / w1.low
      const prevRatio = delta / w1.delta
      const currentRatio = delta / w2.delta
      const lastRatio = delta / wLast.delta
      singleTemp.down[key].push({ delta, deltaPercent, prevRatio, currentRatio, lastRatio })
    }

    singleStats.down[key] = {}
    for (const variant of singleFields) {
      const values = singleTemp.down[key].map(item => item[variant.field])
      const basicStats = calculateStatistics(values, true)

      let clusters
      let clustersIterations = []
      let epsilon = variant.epsilon
      for (let i = 0; i < singleMaxExtendEpsilonSteps; i++) {
        const clustersIter = clusterizeDbscan(values, epsilon, variant.minPointsPercent)
        clustersIterations.push({
          epsilon,
          clusters: clustersIter
        })

        if (clustersIter.noise.length / values.length < singleMaxNoisePercent) {
          clusters = clustersIter
          break
        } else {
          epsilon *= 2
        }
      }

      if (!clusters) {
        clusters = clustersIterations[clustersIterations.length - 1].clusters
      }

      singleStats.down[key][variant.field] = {
        values,
        stats: basicStats,
        clusters,
        coverage: 1 - clusters.noise.length / values.length,
        clustersIterations
      }
    }
  }

  // console.log("getWaveExtremStats single temp:", singleTemp)
  // console.log("getWaveExtremStats single stats:", singleStats)

  // ****************************************************
  // bounce stats
  // ****************************************************

  const pullbackStats = { up: {}, down: {} }
  const pullbackTemp = { up: [], down: [] }

  const pullbackFields = [
    // {
    //   field: 'delta',
    //   epsilon: 0.05, // need to use coin min price step size calculations
    //   minPointsPercent: 0.05
    // },
    {
      field: 'deltaPercent',
      epsilon: 0.0001, // 0.05% ~ average market order fee
      minPointsPercent: 0.01
    },
    {
      field: 'prevRatio',
      epsilon: 0.01,
      minPointsPercent: 0.01
    },
    // {
    //   field: 'currentRatio',
    //   epsilon: 0.025,
    //   minPointsPercent: 0.01
    // },
    {
      field: 'lastRatio',
      epsilon: 0.01,
      minPointsPercent: 0.01
    }
  ]

  const pullbackMaxExtendEpsilonSteps = 5
  const pullbackMaxNoisePercent = 0.15

  for (const result of results.pullbacks.down) {
    const w1 = waves[result.prevIdx]
    const w2 = waves[result.newIdx]
    const delta = w1.high - w2.high
    const wLast = waves[result.newIdx - 1]
    const deltaPercent = delta / w1.high
    const prevRatio = delta / w1.delta
    const currentRatio = delta / w2.delta
    const lastRatio = delta / wLast.delta
    pullbackTemp.down.push({ delta, deltaPercent, prevRatio, currentRatio, lastRatio })
  }

  for (const result of results.pullbacks.up) {
    const w1 = waves[result.prevIdx]
    const w2 = waves[result.newIdx]
    const wLast = waves[result.newIdx - 1]
    const delta = w2.low - w1.low
    const deltaPercent = delta / w1.low
    const prevRatio = delta / w1.delta
    const currentRatio = delta / w2.delta
    const lastRatio = delta / wLast.delta
    pullbackTemp.up.push({ delta, deltaPercent, prevRatio, currentRatio, lastRatio })
  }

  for (const variant of pullbackFields) {
    const values = pullbackTemp.down.map(item => item[variant.field])
    const basicStats = calculateStatistics(values, true)

    let clusters
    let clustersIterations = []
    let epsilon = variant.epsilon
    for (let i = 0; i < pullbackMaxExtendEpsilonSteps; i++) {
      const clustersIter = clusterizeDbscan(values, epsilon, variant.minPointsPercent)
      clustersIterations.push({
        epsilon,
        clusters: clustersIter
      })

      if (clustersIter.noise.length / values.length < pullbackMaxNoisePercent) {
        clusters = clustersIter
        break
      } else {
        epsilon *= 2
      }
    }

    if (!clusters) {
      clusters = clustersIterations[clustersIterations.length - 1].clusters
    }

    pullbackStats.down[variant.field] = {
      values,
      stats: basicStats,
      clusters,
      coverage: 1 - clusters.noise.length / values.length,
      clustersIterations
    }
  }

  for (const variant of pullbackFields) {
    const values = pullbackTemp.up.map(item => item[variant.field])
    const basicStats = calculateStatistics(values, true)

    let clusters
    let clustersIterations = []
    let epsilon = variant.epsilon
    for (let i = 0; i < pullbackMaxExtendEpsilonSteps; i++) {
      const clustersIter = clusterizeDbscan(values, epsilon, variant.minPointsPercent)
      clustersIterations.push({
        epsilon,
        clusters: clustersIter
      })

      if (clustersIter.noise.length / values.length < pullbackMaxNoisePercent) {
        clusters = clustersIter
        break
      } else {
        epsilon *= 2
      }
    }

    if (!clusters) {
      clusters = clustersIterations[clustersIterations.length - 1].clusters
    }

    pullbackStats.up[variant.field] = {
      values,
      stats: basicStats,
      clusters,
      coverage: 1 - clusters.noise.length / values.length,
      clustersIterations
    }
  }

  // console.log("getWaveExtremStats pullbacks temp:", pullbackTemp)
  // console.log("getWaveExtremStats pullbacks stats:", pullbackStats)

  return {
    data: results,
    stats: {
      single: singleStats,
      pullback: pullbackStats,
    }
  }
}

export function getBounceSL(waveExtremStats, direction, alphaCoverage = 5, alphaLimit = 8, key = 'falseBreakouts1') {
  if (!waveExtremStats) return null

  const sideStats = waveExtremStats?.stats?.single[direction]
  const dp = sideStats?.[key]?.deltaPercent

  if (!dp) return null

  const total = dp.values.length
  const initOptions = []

  const stdDevMultipliers = [1, 1.5, 2, 3]
  // 0.683 - 1 sigma, 0.866 - 1.5 sigma, 0.954 - 2 sigma, 0.997 - 3 sigma
  for (const cluster of dp.clusters.stats) {
    for (const multiplier of stdDevMultipliers) {
      const newSL = cluster.avg + cluster.stdDev * multiplier
      const covered = dp.values.filter(val => val <= newSL).length
      const coverage = covered / total
      initOptions.push({
        coverage,
        limit: newSL,
        covered,
        stdDev: multiplier,
      })
    }
  }
  initOptions.sort((a, b) => a.coverage - b.coverage)

  const optimalLimit = findOptimalLimit(initOptions, alphaCoverage, alphaLimit)
  if (!optimalLimit) {
    const sideStats = waveExtremStats?.stats?.pullback[direction]
    const dp = sideStats?.deltaPercent
    if (dp.stats?.avg) {
      return {
        total: null,
        options: null,
        optimal: {
          limit: dp.stats?.avg + dp.stats?.stdDev
        },
      }
    }
    return null
  }

  const { options, optimal } = optimalLimit

  return {
    total,
    options,
    optimal
  }
}

export function getBounceEnter(waveExtremStats, direction, alphaCoverage = 5, alphaLimit = 50) {
  if (!waveExtremStats) return null

  const sideStats = waveExtremStats?.stats?.pullback[direction]
  const dp = sideStats?.deltaPercent

  if (!dp) return null

  const total = dp.values.length
  const initOptions = []

  const stdDevMultipliers = [1, 1.5, 2, 3]
  // 0.683 - 1 sigma, 0.866 - 1.5 sigma, 0.954 - 2 sigma, 0.997 - 3 sigma
  for (const cluster of dp.clusters.stats) {
    for (const multiplier of stdDevMultipliers) {
      const newLimit = cluster.avg + cluster.stdDev * multiplier
      const covered = dp.values.filter(val => val <= newLimit).length
      const coverage = covered / total
      initOptions.push({
        coverage,
        limit: newLimit,
        covered,
        stdDev: multiplier,
      })
    }
  }
  initOptions.sort((a, b) => a.coverage - b.coverage)

  const optimalLimit = findOptimalLimit(initOptions, alphaCoverage, alphaLimit)
  if (!optimalLimit) {
    if (dp.stats?.avg) {
      return {
        total: null,
        options: null,
        optimal: {
          limit: dp.stats?.avg + dp.stats?.stdDev
        },
      }
    }
    return null
  }

  const { options, optimal } = optimalLimit

  return {
    total,
    options,
    optimal,
  }
}

export function findOptimalLimit(options, alphaCoverage = 3, alphaLimit = 6) {
  if (options.length === 0) return null;

  const normalizedOptions = options.map(option => ({
    ...option,
    utilityCoverage: diminishingUtility(option.coverage, alphaCoverage),
    utilityLimit: diminishingUtility(option.limit, alphaLimit),
  }));

  let bestOption = normalizedOptions[0];
  let bestScore = -Infinity;

  for (const option of normalizedOptions) {
    const score = option.utilityCoverage - option.utilityLimit;
    option.score = score

    // console.log('findOptimalLimit', option, score);

    if (score > bestScore) {
      bestScore = score;
      bestOption = option;
    }
  }

  return {
    optimal: bestOption,
    options: normalizedOptions,
  }
}

export function findOptimalWaveWindow(options, alphaVolatility = 5, alphaDuration = 5, minDelta) {
  if (options.length === 0) return null;

  const normalizedOptions = options.map(option => ({
    ...option,
    utilityVolatility: diminishingUtility(option.delta * 10, alphaVolatility),
    utilityDuration: diminishingUtility(option.duration * 50, alphaDuration),
  }));

  let bestOption = normalizedOptions[0];
  let bestScore = -Infinity;

  for (const option of normalizedOptions) {
    const score = option.utilityVolatility - option.utilityDuration;
    option.score = option.delta >= minDelta ? score : -Infinity

    // console.log('findOptimalWaveWindow', option, score);

    if (score > bestScore) {
      bestScore = score;
      bestOption = option;
    }
  }

  return {
    optimal: bestOption,
    options: normalizedOptions,
  }
}

export function diminishingUtility(x, alpha = 5) {
  return 1 - Math.exp(-alpha * x);
}

// ***********************************************

export function getWaveLevels(waves) {
  const highs = []
  const lows = []

  for (let i = 0; i < waves.length; i++) {
    const wave = waves[i];

    if (wave.direction === 'up') {
      for (let j = highs.length - 1; j >= 0; j--) {
        const closestHigh = highs[j];
        if (wave.high > closestHigh.high) {
          highs.pop()
        }
      }
      highs.push(wave)
    } else if (wave.direction === 'down') {
      for (let j = lows.length - 1; j >= 0; j--) {
        const closestLow = lows[j];
        if (wave.low < closestLow.low) {
          lows.pop()
        }
      }
      lows.push(wave)
    }
  }

  return {
    highs,
    lows,
  }
}

// ***********************************************

export const getSequenceStats = (data, maxLength = 4) => {
  const typeMapping = {};
  const directionMapping = {};
  let typeCounter = 0;
  let directionCounter = 0;

  const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const getTypeId = (type) => {
    if (!(type in typeMapping)) {
      typeMapping[type] = typeCounter++;
    }
    return typeMapping[type];
  };

  const getDirectionId = (direction) => {
    if (!(direction in directionMapping)) {
      directionMapping[direction] = directionCounter++;
    }
    return directionMapping[direction];
  };

  const stats = {};
  const totalSequences = Array(maxLength).fill(0);

  for (let i = 1; i < data.length; i++) {
    let sequence = [(getTypeId(data[i].type || 'none') << 8) | getDirectionId(data[i].direction)];

    for (let j = i + 1; j < data.length && sequence.length < maxLength; j++) {
      sequence.push((getTypeId(data[j].type || 'none') << 8) | getDirectionId(data[j].direction));
      const key = sequence.join('->');

      stats[key] = (stats[key] || 0) + 1;
      totalSequences[sequence.length - 1]++;
    }
  }

  const readableStats = Array.from({ length: maxLength }, () => []);
  for (const key in stats) {
    const sequence = key.split('->').map(code => {
      const typeId = code >> 8;
      const directionId = code & 0xFF;

      const type = Object.keys(typeMapping).find(key => typeMapping[key] === typeId);
      const direction = Object.keys(directionMapping).find(key => directionMapping[key] === directionId);

      return `${type}${capitalizeFirstLetter(direction || 'Any')}`;
    });

    const sequenceLength = sequence.length;
    const readableKey = sequence.join(' -> ');
    const frequency = stats[key] / totalSequences[sequenceLength - 1];
    readableStats[sequenceLength - 1].push({ sequence: readableKey, frequency });
  }

  readableStats.forEach(group => group.sort((a, b) => b.frequency - a.frequency));
  readableStats.shift(); // Удаляем первый элемент, содержащий пустую последовательность

  return readableStats;
};

export const predictSequence = (waves, stats) => {
  const maxLength = stats.length;
  const recentSequence = waves.slice(-maxLength);

  const capitalizeFirstLetter = (str) => str.charAt(0).toUpperCase() + str.slice(1);

  const results = [];

  for (let i = 1; i <= recentSequence.length && i <= maxLength; i++) {
    const key = recentSequence
      .slice(-i)
      .map(wave => `${wave.type || 'none'}${capitalizeFirstLetter(wave.direction || 'Any')}`)
      .join(' -> ');

    if (stats[i - 1]) {
      const matches = stats[i - 1].filter(item => item.sequence.startsWith(key));
      if (matches.length > 0) {
        const totalFrequency = matches.reduce((sum, match) => sum + match.frequency, 0);

        const prediction = matches.map(match => {
          const nextWaveSequence = match.sequence.split(' -> ');
          const nextWave = nextWaveSequence[i];

          // Определение типа и направления на основе известных значений
          let nextType = 'none';
          let nextDirection = 'Unknown';

          if (nextWave.startsWith('move')) {
            nextType = 'move';
          } else if (nextWave.startsWith('pullback')) {
            nextType = 'pullback';
          }

          if (nextWave.endsWith('Up')) {
            nextDirection = 'up';
          } else if (nextWave.endsWith('Down')) {
            nextDirection = 'down';
          }

          return {
            nextWave: nextWave || 'Unknown',
            type: nextType,
            direction: nextDirection,
            probability: match.frequency / totalFrequency,
          };
        }).sort((a, b) => b.probability - a.probability);

        const result = {
          sequence: key,
          prediction
        }

        results.push(result);

        // console.log('predictSequence recentSequence', i, key, result.prediction[0])
      }
    }
  }

  return results;
};

// **********************************
//  Utils
// **********************************

export function getMinMaxLevelsAfterTime(levels, time) {
  let maxLevel = null;
  let minLevel = null;

  levels.forEach(level => {
    // если уровень содержит mergedLevels, берем наибольший startTime
    const startTime = level.mergedLevels
      ? Math.max(level.startTime, ...level.mergedLevels.map(l => l.startTime))
      : level.startTime;

    if (startTime >= time) {
      if (!maxLevel || level.level > maxLevel.level) {
        maxLevel = level;
      }
      if (!minLevel || level.level < minLevel.level) {
        minLevel = level;
      }
    }
  });

  return { minLevel, maxLevel };
}


// export function mergeLevels(current, next, useLast) {
//   const isBeatenByNext = current.idx + current.duration === next.idx;
//   const isBeatenByCurrent = next.idx + next.duration === current.idx;
//   const mergeTouches = isBeatenByNext || isBeatenByCurrent;

//   const currentTouches = (!current.unbeat && !mergeTouches)
//     ? 0 : current.touches ? current.touches : 1;
//   const nextTouches = (!next.unbeat && !mergeTouches)
//     ? 0 : next.touches ? next.touches : 1;

//   let mergedLevel
//   if (useLast) {
//     let last = next
//     if (next.idx < current.idx) {
//       last = current
//     }
//     mergedLevel = {
//       level: last.level,
//       duration: last.duration,
//       startTime: last.startTime,
//       touches: currentTouches + nextTouches,
//       lowerBound: last.lowerBound,
//       upperBound: last.upperBound,
//       mergedLevels: [...(current.mergedLevels || [current]), ...(next.mergedLevels || [next])]
//     }
//   } else {
//     mergedLevel = {
//       level: (current.level + next.level) / 2,
//       duration: current.duration + next.duration,
//       startTime: Math.min(current.startTime, next.startTime),
//       touches: currentTouches + nextTouches,
//       lowerBound: Math.min(current.lowerBound, next.lowerBound),
//       upperBound: Math.max(current.upperBound, next.upperBound),
//       mergedLevels: [...(current.mergedLevels || [current]), ...(next.mergedLevels || [next])]
//     }
//   }

//   if (current.unbeat || next.unbeat) mergedLevel.unbeat = true;
//   if (current.type === next.type) {
//     mergedLevel.type = current.type;
//   } else {
//     mergedLevel.type = 'double';
//   }
//   // console.log('mergeLevels', current, next, mergedLevel);
//   return mergedLevel;
// }




// **********************************
//  Pivots
// **********************************


export function Pivots(candleInterval, minuteCandles, chart) {
  const intervalMinutes = intervalToMinutes(candleInterval)
  const pivotMult = 60
  const pivotCandles = convertCandles(minuteCandles, 1, intervalMinutes * pivotMult)

  const pv = new Pivot('fibonacci') //'classic' | 'woodie' | 'camarilla' | 'fibonacci'
  const pivotShift = 60 * intervalMinutes * pivotMult
  let pivotsData = pivotCandles.map(c => {
    const value = pv.nextValue(c.high, c.low, c.close);
    return { time: c.time + pivotShift, value: value }
    //   {
    //     "time": 1686960000,
    //     "value": {
    //         "r3": 264.6066666666666,
    //         "r2": 257.2133333333333,
    //         "r1": 251.90666666666664,
    //         "pp": 244.51333333333332,
    //         "s1": 239.20666666666665,
    //         "s2": 231.81333333333333,
    //         "s3": 226.50666666666666
    //     }
    // }
  })

  // console.log('pivotsData', pivotsData)
  const pivotLevels = {}
  for (let i = 0; i < pivotsData.length; i++) {
    const period = pivotsData[i]
    const time = period.time
    const value = period.value

    for (let key in value) {
      pivotLevels[key] = pivotLevels[key] || []
      pivotLevels[key].push({ time, value: value[key] })
      if (i < pivotsData.length - 1) {
        pivotLevels[key].push({ time: pivotsData[i + 1].time - 1, value: value[key] })
      }
    }
  }
  console.log('pivotLevels', pivotLevels)

  for (const lvl in pivotLevels) {
    const series = chart.addSeries(LineSeries, {
      color: getLevelColor(lvl),
      lineWidth: 1,
      lineStyle: LineStyle.Solid,
      lastValueVisible: false,
      priceLineVisible: false,
    });

    try {
      series.setData(pivotLevels[lvl]);
    } catch (error) {
      console.log('series.setData pivotLevels', error)
    }
  }
}

export function getLevelColor(level) {
  switch (level) {
    case 'r3':
      return 'rgba(190, 90, 120, 0.7)'
    case 'r2':
      return 'rgba(190, 60, 120, 0.8)'
    case 'r1':
      return 'rgba(190, 30, 120, 0.9)'
    case 'pp':
      return 'rgba(30, 140, 10, 1)'
    case 's1':
      return 'rgba(90, 120, 190, 0.9)'
    case 's2':
      return 'rgba(90, 100, 190, 0.8)'
    case 's3':
      return 'rgba(90, 80, 190, 0.7)'
    default:
      return 'rgba(255, 255, 255, 0.5)'
  }
}

export function getLatinLetterByIndex(index) {
  return String.fromCharCode(65 + index);
}

function formatNumber(num, significantDigits = 3) {
  // Включаем массив суффиксов внутрь функции для полной инкапсуляции
  const suffixes = {
    1000: 'K',
    1000000: 'M',
    1000000000: 'B',
    1000000000000: 'T'
  };

  const absNum = Math.abs(num);

  if (absNum >= 1000) {
    let scale = 1000;
    let suffix = '';
    let scaledNum = num;

    // Определяем нужный делитель и суффикс
    while (scaledNum >= 1000 && scale <= 1e12) {
      scale *= 1000;
      scaledNum /= 1000;
      suffix = suffixes[scale] || '';
    }

    // Округляем число в зависимости от величины
    if (scaledNum >= 100) {
      return (Math.round(scaledNum) + suffix);
    } else if (scaledNum >= 10) {
      return (scaledNum.toFixed(1) + suffix);
    } else {
      return (scaledNum.toFixed(2) + suffix);
    }
  } else if (absNum < 1) {
    // Используем toPrecision для чисел меньше 1
    return num.toPrecision(significantDigits);
  } else {
    // Числа от 1 до 999, применяем логику похожую на большие числа, но без суффикса
    if (num >= 100) {
      return Math.round(num).toString();
    } else if (num >= 10) {
      return num.toFixed(1);
    } else {
      return num.toFixed(2);
    }
  }
}
